import React, { useEffect, useState, useCallback } from "react";
import { db } from "../firebase";
import { collection, addDoc, query, where, orderBy, onSnapshot, deleteDoc, doc, setDoc, updateDoc, getDocs } from "firebase/firestore";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import * as XLSX from "xlsx";
import Seite from "../components/Seite";
import { REGISTRIERUNG_URL } from "./FerienlagerAnmeldung";

// ── Gerichte-Datenbank ────────────────────────────────────────────────────────
const STANDARD_GERICHTE = [
  { name: "Schweinelachs", beilagen: ["Nudeln", "Reis"], zutaten: [{ name: "Schweinelachs", mengeProEinheit: "500g", anzahl: "1", gruppe: "Fleisch/Wurst" }, { name: "Zigeunersauce", mengeProEinheit: "1 Glas", anzahl: "1", gruppe: "Saucen/Gewürze" }] },
  { name: "Putenschnitzel", beilagen: ["Salzkartoffeln"], zutaten: [{ name: "Putenschnitzel", mengeProEinheit: "500g", anzahl: "1", gruppe: "Geflügel" }, { name: "Sauce Hollandaise", mengeProEinheit: "1 Glas", anzahl: "1", gruppe: "Saucen/Gewürze" }, { name: "Kartoffeln", mengeProEinheit: "2,5kg Sack", anzahl: "1", gruppe: "Gemüse/Obst" }] },
  { name: "Nudeln mit Käsesauce", beilagen: ["Kochschinken"], zutaten: [{ name: "Nudeln", mengeProEinheit: "500g", anzahl: "1", gruppe: "Nudeln/Reis" }, { name: "Schmelzkäse", mengeProEinheit: "200g", anzahl: "1", gruppe: "Milchprodukte" }, { name: "Sahne", mengeProEinheit: "200ml", anzahl: "1", gruppe: "Milchprodukte" }, { name: "Kochschinken", mengeProEinheit: "200g", anzahl: "1", gruppe: "Fleisch/Wurst" }] },
  { name: "Hähnchenbrustfilet im Knuspermantel", beilagen: ["Püree", "Erbsen & Möhren"], zutaten: [{ name: "Hähnchenbrustfilet", mengeProEinheit: "500g", anzahl: "1", gruppe: "Geflügel" }, { name: "Paniermehl", mengeProEinheit: "400g", anzahl: "1", gruppe: "Backwaren/Brot" }, { name: "Eier", mengeProEinheit: "10er Pack", anzahl: "1", gruppe: "Eier" }, { name: "Kartoffeln", mengeProEinheit: "2,5kg Sack", anzahl: "1", gruppe: "Gemüse/Obst" }, { name: "Erbsen & Möhren", mengeProEinheit: "1 Dose", anzahl: "1", gruppe: "Gemüse/Obst" }] },
  { name: "Hähnchenschnitzel", beilagen: ["Erbsen & Möhren", "Kartoffelpüree"], zutaten: [{ name: "Hähnchenschnitzel", mengeProEinheit: "500g", anzahl: "1", gruppe: "Geflügel" }, { name: "Kartoffeln", mengeProEinheit: "2,5kg Sack", anzahl: "1", gruppe: "Gemüse/Obst" }, { name: "Erbsen & Möhren", mengeProEinheit: "1 Dose", anzahl: "1", gruppe: "Gemüse/Obst" }, { name: "Rahmsauce", mengeProEinheit: "1 Glas", anzahl: "1", gruppe: "Saucen/Gewürze" }] },
  { name: "Gyros", beilagen: ["Reis", "Fladenbrot", "Tzatziki"], zutaten: [{ name: "Gyrosfleisch", mengeProEinheit: "500g", anzahl: "1", gruppe: "Fleisch/Wurst" }, { name: "Reis", mengeProEinheit: "1kg", anzahl: "1", gruppe: "Nudeln/Reis" }, { name: "Fladenbrot", mengeProEinheit: "4 Stück", anzahl: "1", gruppe: "Backwaren/Brot" }, { name: "Tzatziki", mengeProEinheit: "250g", anzahl: "1", gruppe: "Milchprodukte" }] },
  { name: "Hamburger", beilagen: ["Nudelsalat", "Blattsalat"], zutaten: [{ name: "Hackfleisch", mengeProEinheit: "500g", anzahl: "1", gruppe: "Fleisch/Wurst" }, { name: "Hamburgerbrötchen", mengeProEinheit: "6 Stück", anzahl: "1", gruppe: "Backwaren/Brot" }, { name: "Nudeln", mengeProEinheit: "500g", anzahl: "1", gruppe: "Nudeln/Reis" }, { name: "Tomaten", mengeProEinheit: "kg", anzahl: "1", gruppe: "Gemüse/Obst" }, { name: "Gurke", mengeProEinheit: "Stück", anzahl: "1", gruppe: "Gemüse/Obst" }, { name: "Röstzwiebeln", mengeProEinheit: "150g", anzahl: "1", gruppe: "Sonstiges" }] },
  { name: "Hähnchenbollen", beilagen: ["Spätzle", "Blumenkohl", "Apfelmus"], zutaten: [{ name: "Hähnchenkeulen", mengeProEinheit: "kg", anzahl: "1", gruppe: "Geflügel" }, { name: "Spätzle", mengeProEinheit: "500g", anzahl: "1", gruppe: "Nudeln/Reis" }, { name: "Blumenkohl", mengeProEinheit: "Kopf", anzahl: "1", gruppe: "Gemüse/Obst" }, { name: "Apfelmus", mengeProEinheit: "720ml Glas", anzahl: "1", gruppe: "Saucen/Gewürze" }] },
  { name: "Braten", beilagen: ["Knödel", "Rotkohl"], zutaten: [{ name: "Schweinebraten", mengeProEinheit: "kg", anzahl: "1", gruppe: "Fleisch/Wurst" }, { name: "Knödel", mengeProEinheit: "1 Packung", anzahl: "1", gruppe: "Sonstiges" }, { name: "Rotkohl", mengeProEinheit: "720ml Glas", anzahl: "1", gruppe: "Gemüse/Obst" }] },
  { name: "Currywurst", beilagen: ["Pommes"], zutaten: [{ name: "Currywurst", mengeProEinheit: "5er Pack", anzahl: "2", gruppe: "Fleisch/Wurst" }, { name: "Pommes", mengeProEinheit: "2,5kg", anzahl: "1", gruppe: "Tiefkühl" }, { name: "Ketchup", mengeProEinheit: "1 Flasche", anzahl: "1", gruppe: "Saucen/Gewürze" }] },
  { name: "HotDog", beilagen: ["Folienkartoffeln", "Kräuterquark"], zutaten: [{ name: "Hotdog-Würstchen", mengeProEinheit: "5er Pack", anzahl: "2", gruppe: "Fleisch/Wurst" }, { name: "Hotdog-Brötchen", mengeProEinheit: "6er Pack", anzahl: "2", gruppe: "Backwaren/Brot" }, { name: "Kartoffeln", mengeProEinheit: "2,5kg Sack", anzahl: "1", gruppe: "Gemüse/Obst" }, { name: "Kräuterquark", mengeProEinheit: "250g", anzahl: "1", gruppe: "Milchprodukte" }] },
  { name: "Schnitzel", beilagen: ["Spätzle", "Kohlrabi"], zutaten: [{ name: "Schnitzel", mengeProEinheit: "500g", anzahl: "1", gruppe: "Fleisch/Wurst" }, { name: "Spätzle", mengeProEinheit: "500g", anzahl: "1", gruppe: "Nudeln/Reis" }, { name: "Kohlrabi", mengeProEinheit: "Stück", anzahl: "2", gruppe: "Gemüse/Obst" }, { name: "Zigeunersauce", mengeProEinheit: "1 Glas", anzahl: "1", gruppe: "Saucen/Gewürze" }] },
  { name: "Nudeln Bolognese", beilagen: [], zutaten: [{ name: "Nudeln", mengeProEinheit: "500g", anzahl: "1", gruppe: "Nudeln/Reis" }, { name: "Hackfleisch", mengeProEinheit: "500g", anzahl: "1", gruppe: "Fleisch/Wurst" }, { name: "Bolognese-Sauce", mengeProEinheit: "1 Glas", anzahl: "1", gruppe: "Saucen/Gewürze" }, { name: "Parmesan", mengeProEinheit: "100g", anzahl: "1", gruppe: "Milchprodukte" }] },
  { name: "Fischstäbchen", beilagen: ["Kartoffeln", "Spinat"], zutaten: [{ name: "Fischstäbchen", mengeProEinheit: "400g", anzahl: "1", gruppe: "Fisch" }, { name: "Kartoffeln", mengeProEinheit: "2,5kg Sack", anzahl: "1", gruppe: "Gemüse/Obst" }, { name: "Spinat", mengeProEinheit: "750g", anzahl: "1", gruppe: "Tiefkühl" }] },
  { name: "Reibeplätzchen", beilagen: [], zutaten: [{ name: "Kartoffeln", mengeProEinheit: "2,5kg Sack", anzahl: "1", gruppe: "Gemüse/Obst" }, { name: "Eier", mengeProEinheit: "10er Pack", anzahl: "1", gruppe: "Eier" }, { name: "Mehl", mengeProEinheit: "1kg", anzahl: "1", gruppe: "Backwaren/Brot" }, { name: "Apfelmus", mengeProEinheit: "720ml Glas", anzahl: "1", gruppe: "Saucen/Gewürze" }] },
  { name: "Hähnchennuggets", beilagen: [], zutaten: [{ name: "Hähnchennuggets", mengeProEinheit: "1kg", anzahl: "1", gruppe: "Geflügel" }, { name: "Pommes", mengeProEinheit: "2,5kg", anzahl: "1", gruppe: "Tiefkühl" }, { name: "Ketchup", mengeProEinheit: "1 Flasche", anzahl: "1", gruppe: "Saucen/Gewürze" }] },
  { name: "Chili Con Carne", beilagen: [], zutaten: [{ name: "Hackfleisch", mengeProEinheit: "500g", anzahl: "1", gruppe: "Fleisch/Wurst" }, { name: "Kidneybohnen", mengeProEinheit: "1 Dose", anzahl: "2", gruppe: "Gemüse/Obst" }, { name: "Tomaten (Dose)", mengeProEinheit: "400g Dose", anzahl: "2", gruppe: "Gemüse/Obst" }, { name: "Mais", mengeProEinheit: "1 Dose", anzahl: "1", gruppe: "Gemüse/Obst" }, { name: "Reis", mengeProEinheit: "1kg", anzahl: "1", gruppe: "Nudeln/Reis" }] },
  { name: "Putenfleisch in Curry-Pfirsich-Soße", beilagen: ["Reis"], zutaten: [{ name: "Putenstreifen", mengeProEinheit: "500g", anzahl: "1", gruppe: "Geflügel" }, { name: "Pfirsiche (Dose)", mengeProEinheit: "1 Dose", anzahl: "1", gruppe: "Gemüse/Obst" }, { name: "Curry-Sauce", mengeProEinheit: "1 Glas", anzahl: "1", gruppe: "Saucen/Gewürze" }, { name: "Reis", mengeProEinheit: "1kg", anzahl: "1", gruppe: "Nudeln/Reis" }] },
  { name: "Hawaii Toast", beilagen: [], zutaten: [{ name: "Toastbrot", mengeProEinheit: "500g", anzahl: "1", gruppe: "Backwaren/Brot" }, { name: "Kochschinken", mengeProEinheit: "200g", anzahl: "1", gruppe: "Fleisch/Wurst" }, { name: "Ananas (Dose)", mengeProEinheit: "1 Dose", anzahl: "1", gruppe: "Gemüse/Obst" }, { name: "Käse", mengeProEinheit: "400g", anzahl: "1", gruppe: "Milchprodukte" }] },
  { name: "Gebratener Reis/Nudeln", beilagen: [], zutaten: [{ name: "Reis", mengeProEinheit: "1kg", anzahl: "1", gruppe: "Nudeln/Reis" }, { name: "Eier", mengeProEinheit: "10er Pack", anzahl: "1", gruppe: "Eier" }, { name: "Gemüse gemischt", mengeProEinheit: "750g", anzahl: "1", gruppe: "Tiefkühl" }, { name: "Sojasoße", mengeProEinheit: "1 Flasche", anzahl: "1", gruppe: "Saucen/Gewürze" }] },
  { name: "Frühlingsrollen", beilagen: [], zutaten: [{ name: "Frühlingsrollen", mengeProEinheit: "10er Pack", anzahl: "2", gruppe: "Tiefkühl" }, { name: "Süß-Sauer-Sauce", mengeProEinheit: "1 Glas", anzahl: "1", gruppe: "Saucen/Gewürze" }] },
  { name: "Fleischkäse im Brötchen", beilagen: ["Kartoffelsalat", "Obstsalat"], zutaten: [{ name: "Fleischkäse", mengeProEinheit: "kg", anzahl: "1", gruppe: "Fleisch/Wurst" }, { name: "Brötchen", mengeProEinheit: "6er Pack", anzahl: "2", gruppe: "Backwaren/Brot" }, { name: "Kartoffeln", mengeProEinheit: "2,5kg Sack", anzahl: "1", gruppe: "Gemüse/Obst" }, { name: "Mayonnaise", mengeProEinheit: "500ml", anzahl: "1", gruppe: "Saucen/Gewürze" }] },
];

const DESSERTS = ["Apfelmus", "Eis", "Milchreis mit Kirschen", "V-Pudding", "Dosenobst", "Obstsalat", "Brownies", "Donuts", "Bagels", "Schokopudding", "Wackelpudding"];
const PRODUKTGRUPPEN = ["Fleisch", "Wurst/Aufschnitt", "Geflügel", "Fisch", "Gemüse", "Obst", "Tiefkühl", "Backwaren/Brot", "Nudeln/Reis", "Saucen/Gewürze", "Milchprodukte", "Eier", "Getränke", "Reinigung/Haushalt", "Sonstiges"];

const STANDARD_ARTIKEL = [
  // Fleisch
  { name: "Hackfleisch (gemischt)", gruppe: "Fleisch", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Schweineschnitzel", gruppe: "Fleisch", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Schweinebraten", gruppe: "Fleisch", mengeProEinheit: "kg", bemerkung: "" },
  { name: "Schweinelachs", gruppe: "Fleisch", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Rindergulasch", gruppe: "Fleisch", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Speck (gewürfelt)", gruppe: "Fleisch", mengeProEinheit: "150g", bemerkung: "" },
  // Wurst/Aufschnitt
  { name: "Bratwurst", gruppe: "Wurst/Aufschnitt", mengeProEinheit: "5er Pack", bemerkung: "" },
  { name: "Bockwurst", gruppe: "Wurst/Aufschnitt", mengeProEinheit: "5er Pack", bemerkung: "" },
  { name: "Wiener Würstchen", gruppe: "Wurst/Aufschnitt", mengeProEinheit: "5er Pack", bemerkung: "" },
  { name: "Hotdog-Würstchen", gruppe: "Wurst/Aufschnitt", mengeProEinheit: "5er Pack", bemerkung: "" },
  { name: "Currywurst", gruppe: "Wurst/Aufschnitt", mengeProEinheit: "5er Pack", bemerkung: "" },
  { name: "Kochschinken", gruppe: "Wurst/Aufschnitt", mengeProEinheit: "200g", bemerkung: "" },
  { name: "Lyoner", gruppe: "Wurst/Aufschnitt", mengeProEinheit: "200g", bemerkung: "" },
  { name: "Salami", gruppe: "Wurst/Aufschnitt", mengeProEinheit: "100g", bemerkung: "" },
  { name: "Leberwurst", gruppe: "Wurst/Aufschnitt", mengeProEinheit: "200g", bemerkung: "" },
  { name: "Fleischkäse", gruppe: "Wurst/Aufschnitt", mengeProEinheit: "kg", bemerkung: "" },
  // Geflügel
  { name: "Hähnchenbrust", gruppe: "Geflügel", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Hähnchenschenkel", gruppe: "Geflügel", mengeProEinheit: "kg", bemerkung: "" },
  { name: "Hähnchenkeulen", gruppe: "Geflügel", mengeProEinheit: "kg", bemerkung: "" },
  { name: "Hähnchenschnitzel", gruppe: "Geflügel", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Hähnchennuggets", gruppe: "Geflügel", mengeProEinheit: "1kg", bemerkung: "" },
  { name: "Putenstreifen", gruppe: "Geflügel", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Putenschnitzel", gruppe: "Geflügel", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Gyrosfleisch", gruppe: "Geflügel", mengeProEinheit: "500g", bemerkung: "" },
  // Fisch
  { name: "Fischstäbchen", gruppe: "Fisch", mengeProEinheit: "400g", bemerkung: "" },
  { name: "Lachsfilet", gruppe: "Fisch", mengeProEinheit: "400g", bemerkung: "" },
  { name: "Thunfisch (Dose)", gruppe: "Fisch", mengeProEinheit: "185g", bemerkung: "" },
  // Gemüse
  { name: "Kartoffeln", gruppe: "Gemüse", mengeProEinheit: "2,5kg Sack", bemerkung: "" },
  { name: "Zwiebeln", gruppe: "Gemüse", mengeProEinheit: "1kg Netz", bemerkung: "" },
  { name: "Knoblauch", gruppe: "Gemüse", mengeProEinheit: "Knolle", bemerkung: "" },
  { name: "Möhren", gruppe: "Gemüse", mengeProEinheit: "1kg", bemerkung: "" },
  { name: "Paprika (rot)", gruppe: "Gemüse", mengeProEinheit: "Stück", bemerkung: "" },
  { name: "Paprika (gelb)", gruppe: "Gemüse", mengeProEinheit: "Stück", bemerkung: "" },
  { name: "Zucchini", gruppe: "Gemüse", mengeProEinheit: "Stück", bemerkung: "" },
  { name: "Tomaten", gruppe: "Gemüse", mengeProEinheit: "kg", bemerkung: "" },
  { name: "Gurke", gruppe: "Gemüse", mengeProEinheit: "Stück", bemerkung: "" },
  { name: "Eisbergsalat", gruppe: "Gemüse", mengeProEinheit: "Kopf", bemerkung: "" },
  { name: "Brokkoli", gruppe: "Gemüse", mengeProEinheit: "Kopf", bemerkung: "" },
  { name: "Blumenkohl", gruppe: "Gemüse", mengeProEinheit: "Kopf", bemerkung: "" },
  { name: "Kohlrabi", gruppe: "Gemüse", mengeProEinheit: "Stück", bemerkung: "" },
  { name: "Erbsen & Möhren (Dose)", gruppe: "Gemüse", mengeProEinheit: "400g Dose", bemerkung: "" },
  { name: "Mais (Dose)", gruppe: "Gemüse", mengeProEinheit: "425ml Dose", bemerkung: "" },
  { name: "Kidneybohnen (Dose)", gruppe: "Gemüse", mengeProEinheit: "400g Dose", bemerkung: "" },
  { name: "Tomaten (Dose)", gruppe: "Gemüse", mengeProEinheit: "400g Dose", bemerkung: "" },
  { name: "Champignons", gruppe: "Gemüse", mengeProEinheit: "400g", bemerkung: "" },
  { name: "Rotkohl (Glas)", gruppe: "Gemüse", mengeProEinheit: "720ml Glas", bemerkung: "" },
  { name: "Sauerkraut", gruppe: "Gemüse", mengeProEinheit: "400g Dose", bemerkung: "" },
  // Obst
  { name: "Äpfel", gruppe: "Obst", mengeProEinheit: "1kg Beutel", bemerkung: "" },
  { name: "Bananen", gruppe: "Obst", mengeProEinheit: "1kg", bemerkung: "" },
  { name: "Orangen", gruppe: "Obst", mengeProEinheit: "1kg", bemerkung: "" },
  { name: "Trauben", gruppe: "Obst", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Erdbeeren", gruppe: "Obst", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Ananas (Dose)", gruppe: "Obst", mengeProEinheit: "825g Dose", bemerkung: "" },
  { name: "Pfirsiche (Dose)", gruppe: "Obst", mengeProEinheit: "820g Dose", bemerkung: "" },
  { name: "Apfelmus", gruppe: "Obst", mengeProEinheit: "720ml Glas", bemerkung: "" },
  // Tiefkühl
  { name: "Pommes Frites", gruppe: "Tiefkühl", mengeProEinheit: "2,5kg", bemerkung: "" },
  { name: "Gemüse gemischt (TK)", gruppe: "Tiefkühl", mengeProEinheit: "750g", bemerkung: "" },
  { name: "Erbsen (TK)", gruppe: "Tiefkühl", mengeProEinheit: "750g", bemerkung: "" },
  { name: "Spinat (TK)", gruppe: "Tiefkühl", mengeProEinheit: "750g", bemerkung: "" },
  { name: "Frühlingsrollen", gruppe: "Tiefkühl", mengeProEinheit: "10er Pack", bemerkung: "" },
  // Backwaren/Brot
  { name: "Toastbrot", gruppe: "Backwaren/Brot", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Weißbrot", gruppe: "Backwaren/Brot", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Vollkornbrot", gruppe: "Backwaren/Brot", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Brötchen", gruppe: "Backwaren/Brot", mengeProEinheit: "6er Pack", bemerkung: "" },
  { name: "Hamburgerbrötchen", gruppe: "Backwaren/Brot", mengeProEinheit: "6er Pack", bemerkung: "" },
  { name: "Hotdog-Brötchen", gruppe: "Backwaren/Brot", mengeProEinheit: "6er Pack", bemerkung: "" },
  { name: "Fladenbrot", gruppe: "Backwaren/Brot", mengeProEinheit: "4 Stück", bemerkung: "" },
  { name: "Paniermehl", gruppe: "Backwaren/Brot", mengeProEinheit: "400g", bemerkung: "" },
  { name: "Mehl (Type 405)", gruppe: "Backwaren/Brot", mengeProEinheit: "1kg", bemerkung: "" },
  { name: "Zucker", gruppe: "Backwaren/Brot", mengeProEinheit: "1kg", bemerkung: "" },
  { name: "Knödel", gruppe: "Backwaren/Brot", mengeProEinheit: "1 Packung", bemerkung: "" },
  // Nudeln/Reis
  { name: "Nudeln (Spaghetti)", gruppe: "Nudeln/Reis", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Nudeln (Penne)", gruppe: "Nudeln/Reis", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Nudeln (Spirelli)", gruppe: "Nudeln/Reis", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Lasagneplatten", gruppe: "Nudeln/Reis", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Reis (Langkorn)", gruppe: "Nudeln/Reis", mengeProEinheit: "1kg", bemerkung: "" },
  { name: "Reis (Rundkorn)", gruppe: "Nudeln/Reis", mengeProEinheit: "1kg", bemerkung: "" },
  { name: "Spätzle", gruppe: "Nudeln/Reis", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Couscous", gruppe: "Nudeln/Reis", mengeProEinheit: "500g", bemerkung: "" },
  // Saucen/Gewürze
  { name: "Ketchup", gruppe: "Saucen/Gewürze", mengeProEinheit: "500ml", bemerkung: "" },
  { name: "Mayonnaise", gruppe: "Saucen/Gewürze", mengeProEinheit: "500ml", bemerkung: "" },
  { name: "Senf (mittelscharf)", gruppe: "Saucen/Gewürze", mengeProEinheit: "200ml", bemerkung: "" },
  { name: "Sojasoße", gruppe: "Saucen/Gewürze", mengeProEinheit: "250ml", bemerkung: "" },
  { name: "Tomatenmark", gruppe: "Saucen/Gewürze", mengeProEinheit: "200g", bemerkung: "" },
  { name: "Bolognese-Sauce", gruppe: "Saucen/Gewürze", mengeProEinheit: "400ml Glas", bemerkung: "" },
  { name: "Tomatensugo", gruppe: "Saucen/Gewürze", mengeProEinheit: "400ml Glas", bemerkung: "" },
  { name: "Zigeunersauce", gruppe: "Saucen/Gewürze", mengeProEinheit: "400ml Glas", bemerkung: "" },
  { name: "Rahmsauce", gruppe: "Saucen/Gewürze", mengeProEinheit: "400ml Glas", bemerkung: "" },
  { name: "Curry-Sauce", gruppe: "Saucen/Gewürze", mengeProEinheit: "400ml Glas", bemerkung: "" },
  { name: "Sauce Hollandaise", gruppe: "Saucen/Gewürze", mengeProEinheit: "250ml Glas", bemerkung: "" },
  { name: "Süß-Sauer-Sauce", gruppe: "Saucen/Gewürze", mengeProEinheit: "300ml Glas", bemerkung: "" },
  { name: "Gemüsebrühe", gruppe: "Saucen/Gewürze", mengeProEinheit: "500ml", bemerkung: "" },
  { name: "Hühnerbrühe", gruppe: "Saucen/Gewürze", mengeProEinheit: "500ml", bemerkung: "" },
  { name: "Öl (Sonnenblumen)", gruppe: "Saucen/Gewürze", mengeProEinheit: "1L", bemerkung: "" },
  { name: "Olivenöl", gruppe: "Saucen/Gewürze", mengeProEinheit: "500ml", bemerkung: "" },
  { name: "Essig", gruppe: "Saucen/Gewürze", mengeProEinheit: "750ml", bemerkung: "" },
  { name: "Salz", gruppe: "Saucen/Gewürze", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Pfeffer (gemahlen)", gruppe: "Saucen/Gewürze", mengeProEinheit: "50g", bemerkung: "" },
  { name: "Paprikapulver", gruppe: "Saucen/Gewürze", mengeProEinheit: "50g", bemerkung: "" },
  { name: "Currypulver", gruppe: "Saucen/Gewürze", mengeProEinheit: "50g", bemerkung: "" },
  { name: "Oregano", gruppe: "Saucen/Gewürze", mengeProEinheit: "20g", bemerkung: "" },
  { name: "Basilikum", gruppe: "Saucen/Gewürze", mengeProEinheit: "20g", bemerkung: "" },
  { name: "Knoblauchpulver", gruppe: "Saucen/Gewürze", mengeProEinheit: "50g", bemerkung: "" },
  { name: "Röstzwiebeln", gruppe: "Saucen/Gewürze", mengeProEinheit: "150g", bemerkung: "" },
  { name: "Essiggurken", gruppe: "Saucen/Gewürze", mengeProEinheit: "720ml Glas", bemerkung: "" },
  // Milchprodukte
  { name: "Milch (3,5%)", gruppe: "Milchprodukte", mengeProEinheit: "1L", bemerkung: "" },
  { name: "Butter", gruppe: "Milchprodukte", mengeProEinheit: "250g", bemerkung: "" },
  { name: "Margarine", gruppe: "Milchprodukte", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Joghurt (natur)", gruppe: "Milchprodukte", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Schlagsahne", gruppe: "Milchprodukte", mengeProEinheit: "200ml", bemerkung: "" },
  { name: "Schmand", gruppe: "Milchprodukte", mengeProEinheit: "200g", bemerkung: "" },
  { name: "Frischkäse", gruppe: "Milchprodukte", mengeProEinheit: "200g", bemerkung: "" },
  { name: "Schmelzkäse", gruppe: "Milchprodukte", mengeProEinheit: "200g", bemerkung: "" },
  { name: "Gouda (Scheiben)", gruppe: "Milchprodukte", mengeProEinheit: "400g", bemerkung: "" },
  { name: "Käse (gerieben)", gruppe: "Milchprodukte", mengeProEinheit: "150g", bemerkung: "" },
  { name: "Parmesan", gruppe: "Milchprodukte", mengeProEinheit: "100g", bemerkung: "" },
  { name: "Mozzarella", gruppe: "Milchprodukte", mengeProEinheit: "125g", bemerkung: "" },
  { name: "Quark (mager)", gruppe: "Milchprodukte", mengeProEinheit: "500g", bemerkung: "" },
  { name: "Kräuterquark", gruppe: "Milchprodukte", mengeProEinheit: "250g", bemerkung: "" },
  { name: "Tzatziki", gruppe: "Milchprodukte", mengeProEinheit: "250g", bemerkung: "" },
  { name: "Pudding (Vanille)", gruppe: "Milchprodukte", mengeProEinheit: "4er Pack", bemerkung: "" },
  { name: "Milchreis", gruppe: "Milchprodukte", mengeProEinheit: "500g", bemerkung: "" },
  // Eier
  { name: "Eier", gruppe: "Eier", mengeProEinheit: "10er Pack", bemerkung: "" },
  // Getränke
  { name: "Wasser (still)", gruppe: "Getränke", mengeProEinheit: "6×1,5L Kasten", bemerkung: "" },
  { name: "Wasser (sprudelnd)", gruppe: "Getränke", mengeProEinheit: "6×1,5L Kasten", bemerkung: "" },
  { name: "Apfelsaft", gruppe: "Getränke", mengeProEinheit: "1L", bemerkung: "" },
  { name: "Orangensaft", gruppe: "Getränke", mengeProEinheit: "1L", bemerkung: "" },
  { name: "Multivitaminsaft", gruppe: "Getränke", mengeProEinheit: "1L", bemerkung: "" },
  { name: "Kakao (Pulver)", gruppe: "Getränke", mengeProEinheit: "400g", bemerkung: "" },
  { name: "Tee (Beutel)", gruppe: "Getränke", mengeProEinheit: "25er Pack", bemerkung: "" },
  { name: "Cola", gruppe: "Getränke", mengeProEinheit: "6×0,5L", bemerkung: "" },
  { name: "Limo (assortiert)", gruppe: "Getränke", mengeProEinheit: "6×0,5L", bemerkung: "" },
  // Reinigung/Haushalt
  { name: "Spülmittel", gruppe: "Reinigung/Haushalt", mengeProEinheit: "500ml", bemerkung: "" },
  { name: "Alufolie", gruppe: "Reinigung/Haushalt", mengeProEinheit: "Rolle", bemerkung: "" },
  { name: "Backpapier", gruppe: "Reinigung/Haushalt", mengeProEinheit: "Rolle", bemerkung: "" },
  { name: "Gefrierbeutel", gruppe: "Reinigung/Haushalt", mengeProEinheit: "20er Pack", bemerkung: "" },
  { name: "Müllbeutel", gruppe: "Reinigung/Haushalt", mengeProEinheit: "10er Pack", bemerkung: "" },
  { name: "Küchenpapier", gruppe: "Reinigung/Haushalt", mengeProEinheit: "3er Pack", bemerkung: "" },
  { name: "Taschentücher", gruppe: "Reinigung/Haushalt", mengeProEinheit: "4er Pack", bemerkung: "" },
];
const AUSGABEN_KATEGORIEN = ["Lebensmittel", "Getränke", "Material", "Transport", "Unterkunft", "Sonstiges"];
const WOCHENTAGE_KURZ = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

function tagVonDatum(vonDatum, index) {
  if (!vonDatum) return null;
  const d = new Date(vonDatum);
  d.setDate(d.getDate() + index);
  return WOCHENTAGE_KURZ[d.getDay()];
}

const s = {
  tabs: { display: "flex", gap: "0.3rem", marginBottom: "1.2rem", flexWrap: "wrap" },
  tab: { padding: "0.5rem 1rem", border: "1px solid #ddd", borderRadius: "20px", cursor: "pointer", fontSize: "0.85rem", backgroundColor: "white", color: "#555" },
  tabAktiv: { backgroundColor: "#1a73e8", color: "white", borderColor: "#1a73e8" },
  card: { backgroundColor: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", padding: "1rem", marginBottom: "0.8rem" },
  row: { display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" },
  input: { padding: "0.45rem 0.5rem", border: "1px solid #ccc", borderRadius: "4px", fontSize: "0.88rem" },
  select: { padding: "0.45rem 0.5rem", border: "1px solid #ccc", borderRadius: "4px", fontSize: "0.88rem" },
  btn: { padding: "0.5rem 1rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem" },
  btnSm: { padding: "0.3rem 0.6rem", backgroundColor: "#f0f2f5", border: "1px solid #ddd", borderRadius: "4px", cursor: "pointer", fontSize: "0.82rem" },
  btnDanger: { padding: "0.3rem 0.6rem", backgroundColor: "#ea4335", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "0.82rem" },
  btnGreen: { padding: "0.45rem 0.9rem", backgroundColor: "#34a853", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem" },
  label: { fontSize: "0.78rem", color: "#666", marginBottom: "0.15rem", display: "block" },
  h3: { margin: "0 0 0.8rem 0", fontSize: "1rem", color: "#1a3558" },
  gruppeTitle: { fontWeight: "600", color: "#1a3558", fontSize: "0.85rem", padding: "0.3rem 0.6rem", borderBottom: "1px solid #e8f0fe", marginBottom: "0.2rem", backgroundColor: "#f0f4ff", borderRadius: "4px 4px 0 0" },
  tag: { fontSize: "0.75rem", backgroundColor: "#e8f0fe", color: "#1a73e8", padding: "0.15rem 0.5rem", borderRadius: "10px", whiteSpace: "nowrap" },
  budget: { display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem" },
  budgetBox: { flex: 1, minWidth: "120px", padding: "0.8rem", borderRadius: "8px", textAlign: "center" },
  dragHandle: { cursor: "grab", color: "#bbb", fontSize: "1.1rem", userSelect: "none", flexShrink: 0 },
  checkboxRow: { display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.85rem", flex: 1 },
  table: { width: "100%", borderCollapse: "collapse", backgroundColor: "white", borderRadius: "8px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  th: { backgroundColor: "#1a3558", color: "white", textAlign: "left", padding: "0.5rem 0.7rem", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.3px" },
  td: { padding: "0.5rem 0.7rem", borderBottom: "1px solid #eee", fontSize: "0.83rem", verticalAlign: "top" },
};

// ── Zutat-Autocomplete ─────────────────────────────────────────────────────────
function ZutatZeile({ zutat, onChange, onRemove, artikel }) {
  const [dropOffen, setDropOffen] = useState(false);
  const gefiltert = zutat.name.length > 0
    ? artikel.filter((a) => a.name.toLowerCase().includes(zutat.name.toLowerCase())).slice(0, 8)
    : [];

  return (
    <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.4rem", flexWrap: "wrap", alignItems: "flex-start" }}>
      <div style={{ position: "relative", flex: 1, minWidth: "130px" }}>
        <input style={{ ...s.input, width: "100%" }} placeholder="Zutat..." value={zutat.name}
          onChange={(e) => { onChange({ ...zutat, name: e.target.value }); setDropOffen(true); }}
          onFocus={() => setDropOffen(true)} onBlur={() => setTimeout(() => setDropOffen(false), 200)} />
        {dropOffen && gefiltert.length > 0 && (
          <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200, backgroundColor: "white", border: "1px solid #ddd", borderRadius: "4px", boxShadow: "0 4px 12px rgba(0,0,0,0.12)", maxHeight: "160px", overflowY: "auto" }}>
            {gefiltert.map((a) => (
              <div key={a.id} onMouseDown={() => { onChange({ ...zutat, name: a.name, gruppe: a.gruppe, mengeProEinheit: a.mengeProEinheit || "" }); setDropOffen(false); }}
                style={{ padding: "0.35rem 0.7rem", cursor: "pointer", fontSize: "0.83rem", borderBottom: "1px solid #f5f5f5" }}>
                <b>{a.name}</b> <span style={{ color: "#888" }}>{a.mengeProEinheit} · {a.gruppe}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <input style={{ ...s.input, width: "90px" }} placeholder="Gebinde" value={zutat.mengeProEinheit || ""} onChange={(e) => onChange({ ...zutat, mengeProEinheit: e.target.value })} title="Gebindegröße" />
      <input style={{ ...s.input, width: "52px" }} placeholder="Anz." type="number" min="1" value={zutat.anzahl || "1"} onChange={(e) => onChange({ ...zutat, anzahl: e.target.value })} title="Anzahl" />
      <input style={{ ...s.input, width: "130px" }} list="dl-gruppen-zutat" placeholder="Kategorie..." value={zutat.gruppe || ""} onChange={(e) => onChange({ ...zutat, gruppe: e.target.value })} />
      <datalist id="dl-gruppen-zutat">{PRODUKTGRUPPEN.map((g) => <option key={g} value={g} />)}</datalist>
      <button type="button" style={s.btnDanger} onClick={onRemove}>✕</button>
    </div>
  );
}

// ── Lager-Auswahl ──────────────────────────────────────────────────────────────
function LagerAuswahl({ user, onSelect }) {
  const [lager, setLager] = useState([]);
  const [neuerName, setNeuerName] = useState("");
  const [von, setVon] = useState("");
  const [bis, setBis] = useState("");
  const [teilnehmer, setTeilnehmer] = useState("");

  useEffect(() => {
    const q = query(collection(db, "ferienlager"), where("uid", "==", user.uid));
    return onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.erstellt?.toMillis?.() || 0) - (a.erstellt?.toMillis?.() || 0));
      setLager(data);
    });
  }, [user.uid]);

  const erstellen = async (e) => {
    e.preventDefault();
    if (!neuerName.trim()) return;
    await addDoc(collection(db, "ferienlager"), {
      name: neuerName, von, bis, teilnehmer: Number(teilnehmer) || 0,
      uid: user.uid, erstellt: new Date(), speiseplan: [], einkaufsliste: [], ausgaben: [], todos: [], budget: 0,
      kuechenartikel: [], lebensmittelVorhanden: [],
    });
    setNeuerName(""); setVon(""); setBis(""); setTeilnehmer("");
  };

  return (
    <div>
      <div style={s.card}>
        <h3 style={s.h3}>Neues Lager erstellen</h3>
        <form onSubmit={erstellen} style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <input style={{ ...s.input, flex: 1, minWidth: "150px" }} placeholder="Lagername..." value={neuerName} onChange={(e) => setNeuerName(e.target.value)} />
          <input style={s.input} type="date" value={von} onChange={(e) => setVon(e.target.value)} title="Von" />
          <input style={s.input} type="date" value={bis} onChange={(e) => setBis(e.target.value)} title="Bis" />
          <input style={{ ...s.input, width: "80px" }} type="number" placeholder="Pers." value={teilnehmer} onChange={(e) => setTeilnehmer(e.target.value)} />
          <button type="submit" style={s.btn}>Erstellen</button>
        </form>
      </div>
      {lager.map((l) => (
        <div key={l.id} style={{ ...s.card, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }} onClick={() => onSelect(l)}>
          <div>
            <div style={{ fontWeight: "600", color: "#1a3558" }}>🏕️ {l.name}</div>
            <div style={{ fontSize: "0.8rem", color: "#888" }}>
              {l.von && l.bis ? `${l.von} – ${l.bis}` : ""}{l.teilnehmer ? ` · ${l.teilnehmer} Personen` : ""}
            </div>
          </div>
          <button style={s.btnSm}>Öffnen →</button>
        </div>
      ))}
      {lager.length === 0 && <p style={{ color: "#999", textAlign: "center", marginTop: "2rem" }}>Noch kein Lager angelegt.</p>}
    </div>
  );
}

// ── Sortierbare Speiseplan-Zeile ───────────────────────────────────────────────
function SortableEintrag({ eintrag, alleGerichte, onAendern, onLoeschen, hatDatum }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: eintrag.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const [edit, setEdit] = useState(false);
  const [data, setData] = useState({ ...eintrag });

  const speichern = () => { onAendern(data); setEdit(false); };

  const gerichtObj = alleGerichte.find((g) => g.name === eintrag.gericht);
  const beilagenOptions = gerichtObj?.beilagen || [];
  const istSub = !!eintrag.istUnterEintrag;

  return (
    <div
      ref={setNodeRef}
      style={{
        ...s.card, padding: "0.5rem 0.8rem", marginBottom: "0.3rem", ...style,
        marginLeft: istSub ? "2rem" : "0",
        borderLeft: istSub ? "3px solid #a8c4e0" : "3px solid transparent",
        backgroundColor: istSub ? "#f8fbff" : "white",
      }}
    >
      {edit ? (
        <div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
            {!hatDatum && (
              <select style={{ ...s.select, width: "60px" }} value={data.tag || ""} onChange={(e) => setData({ ...data, tag: e.target.value })}>
                {["Mo","Di","Mi","Do","Fr","Sa","So"].map((t) => <option key={t}>{t}</option>)}
              </select>
            )}
            <div style={{ flex: 1, minWidth: "140px" }}>
              <label style={s.label}>Gericht</label>
              <input style={{ ...s.input, width: "100%" }} list={`dl-gerichte-${eintrag.id}`} value={data.gericht || ""} onChange={(e) => setData({ ...data, gericht: e.target.value })} />
              <datalist id={`dl-gerichte-${eintrag.id}`}>{alleGerichte.map((g) => <option key={g.name} value={g.name} />)}</datalist>
            </div>
            <div style={{ flex: 1, minWidth: "120px" }}>
              <label style={s.label}>Beilage</label>
              <input style={{ ...s.input, width: "100%" }} list={`dl-beilagen-${eintrag.id}`} value={data.beilage || ""} onChange={(e) => setData({ ...data, beilage: e.target.value })} placeholder="Beilage..." />
              <datalist id={`dl-beilagen-${eintrag.id}`}>{beilagenOptions.map((b) => <option key={b} value={b} />)}</datalist>
            </div>
            <div style={{ flex: 1, minWidth: "100px" }}>
              <label style={s.label}>Dessert</label>
              <input style={{ ...s.input, width: "100%" }} list={`dl-desserts-${eintrag.id}`} value={data.dessert || ""} onChange={(e) => setData({ ...data, dessert: e.target.value })} placeholder="Dessert..." />
              <datalist id={`dl-desserts-${eintrag.id}`}>{DESSERTS.map((d) => <option key={d} value={d} />)}</datalist>
            </div>
            <div style={{ flex: 2, minWidth: "140px" }}>
              <label style={s.label}>Bemerkung</label>
              <input style={{ ...s.input, width: "100%" }} value={data.bemerkung || ""} onChange={(e) => setData({ ...data, bemerkung: e.target.value })} placeholder="Bemerkung..." />
            </div>
          </div>
          <div style={s.row}>
            <button style={s.btnGreen} onClick={speichern}>💾 Speichern</button>
            <button style={s.btnSm} onClick={() => { setEdit(false); setData({ ...eintrag }); }}>Abbrechen</button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ ...s.dragHandle, fontSize: istSub ? "0.85rem" : "1rem", color: istSub ? "#a8c4e0" : "#888" }} {...attributes} {...listeners}>⠿</span>
          {!istSub && <span style={s.tag}>{eintrag.tag}</span>}
          {istSub && <span style={{ fontSize: "0.72rem", color: "#a8c4e0", fontWeight: 600 }}>↳</span>}
          <span style={{ fontWeight: istSub ? 500 : 600, color: istSub ? "#3a6090" : "#1a3558", flex: 1, minWidth: "100px", fontSize: istSub ? "0.88rem" : "1rem" }}>{eintrag.gericht || "—"}</span>
          {eintrag.beilage && <span style={{ fontSize: "0.78rem", color: "#555" }}>🥗 {eintrag.beilage}</span>}
          {eintrag.dessert && <span style={{ fontSize: "0.78rem", color: "#555" }}>🍮 {eintrag.dessert}</span>}
          {eintrag.bemerkung && <span style={{ fontSize: "0.76rem", color: "#888", fontStyle: "italic" }}>📝 {eintrag.bemerkung}</span>}
          <button style={s.btnSm} onClick={() => { setEdit(true); setData({ ...eintrag }); }}>✏️</button>
          <button style={s.btnDanger} onClick={onLoeschen}>🗑️</button>
        </div>
      )}
    </div>
  );
}

// ── Speiseplan-Tab ─────────────────────────────────────────────────────────────
function SpeiseplanTab({ lager, gerichte, onUpdate }) {
  const [plan, setPlan] = useState(lager.speiseplan || []);
  const [neuesGericht, setNeuesGericht] = useState("");
  const [neueBeilage, setNeueBeilage] = useState("");
  const [neuerDessert, setNeuerDessert] = useState("");
  const [neueBemerkung, setNeueBemerkung] = useState("");
  const [review, setReview] = useState(lager.kuechenReview || "");
  const [reviewGespeichert, setReviewGespeichert] = useState(true);
  const sensors = useSensors(useSensor(PointerSensor));
  const hatDatum = !!lager.von;

  const speichern = useCallback(async (neuerPlan) => {
    setPlan(neuerPlan);
    await updateDoc(doc(db, "ferienlager", lager.id), { speiseplan: neuerPlan });
    onUpdate({ ...lager, speiseplan: neuerPlan });
  }, [lager, onUpdate]);

  // Sub-Einträge erben den Tag des vorherigen Haupteintrags; Tage werden nur für Haupteinträge hochgezählt
  const tagsNeuberechnen = (arr) => {
    let hauptIdx = 0;
    let letzterTag = "Mo";
    return arr.map((e) => {
      if (e.istUnterEintrag) return { ...e, tag: letzterTag };
      const tag = hatDatum ? tagVonDatum(lager.von, hauptIdx) : e.tag;
      letzterTag = tag;
      hauptIdx++;
      return { ...e, tag };
    });
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldI = plan.findIndex((p) => p.id === active.id);
    const newI = plan.findIndex((p) => p.id === over.id);
    const neu = tagsNeuberechnen(arrayMove(plan, oldI, newI));
    await speichern(neu);
  };

  const hinzufuegen = async (e) => {
    e.preventDefault();
    if (!neuesGericht.trim()) return;
    const hauptAnzahl = plan.filter((p) => !p.istUnterEintrag).length;
    const tag = hatDatum ? tagVonDatum(lager.von, hauptAnzahl) : "Mo";
    const eintrag = { id: Date.now().toString(), tag, gericht: neuesGericht, beilage: neueBeilage, dessert: neuerDessert, bemerkung: neueBemerkung };
    await speichern(tagsNeuberechnen([...plan, eintrag]));
    setNeuesGericht(""); setNeueBeilage(""); setNeuerDessert(""); setNeueBemerkung("");
  };

  const zwischenEinfuegen = async (nachIndex) => {
    const vorgaenger = plan[nachIndex];
    const neuerEintrag = { id: Date.now().toString(), tag: vorgaenger?.tag || "Mo", gericht: "", beilage: "", dessert: "", bemerkung: "", istUnterEintrag: true };
    const neu = [...plan.slice(0, nachIndex + 1), neuerEintrag, ...plan.slice(nachIndex + 1)];
    await speichern(tagsNeuberechnen(neu));
  };

  const eintragAendern = (id, data) => speichern(tagsNeuberechnen(plan.map((p) => p.id === id ? { ...data } : p)));
  const entfernen = (id) => speichern(tagsNeuberechnen(plan.filter((p) => p.id !== id)));

  const reviewSpeichern = async () => {
    await updateDoc(doc(db, "ferienlager", lager.id), { kuechenReview: review });
    onUpdate({ ...lager, kuechenReview: review });
    setReviewGespeichert(true);
  };

  const reviewKeyDown = (e) => {
    if (e.key === "Enter") {
      const ta = e.target;
      const pos = ta.selectionStart;
      const zeilen = ta.value.substring(0, pos).split("\n");
      const aktuelleZeile = zeilen[zeilen.length - 1];
      if (aktuelleZeile.startsWith("• ") || aktuelleZeile.startsWith("- ")) {
        if (aktuelleZeile === "• " || aktuelleZeile === "- ") {
          // Leerer Bullet → Bullet entfernen
          e.preventDefault();
          const prefix = aktuelleZeile;
          const vorher = ta.value.substring(0, pos - prefix.length);
          const nachher = ta.value.substring(pos);
          const neuerWert = vorher + "\n" + nachher;
          setReview(neuerWert);
          setTimeout(() => { ta.selectionStart = ta.selectionEnd = vorher.length + 1; }, 0);
        } else {
          e.preventDefault();
          const bullet = aktuelleZeile.startsWith("• ") ? "• " : "- ";
          const vorher = ta.value.substring(0, pos);
          const nachher = ta.value.substring(pos);
          const neuerWert = vorher + "\n" + bullet + nachher;
          setReview(neuerWert);
          setTimeout(() => { ta.selectionStart = ta.selectionEnd = pos + 1 + bullet.length; }, 0);
        }
      }
    }
  };

  const gerichtObj = gerichte.find((g) => g.name === neuesGericht);
  const beilagenOptions = gerichtObj?.beilagen || [];

  return (
    <div>
      <div style={{ ...s.row, marginBottom: "1rem" }}>
        {hatDatum && <span style={{ ...s.tag, backgroundColor: "#e6f4ea", color: "#34a853" }}>Tage werden aus Datum berechnet</span>}
        <span style={{ fontSize: "0.8rem", color: "#888" }}>{plan.length} Einträge</span>
      </div>

      <div style={s.card}>
        <form onSubmit={hinzufuegen}>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.4rem" }}>
            <div style={{ flex: 2, minWidth: "140px" }}>
              <label style={s.label}>Gericht</label>
              <input style={{ ...s.input, width: "100%" }} list="dl-g-neu" value={neuesGericht} onChange={(e) => setNeuesGericht(e.target.value)} placeholder="Gericht..." />
              <datalist id="dl-g-neu">{gerichte.map((g) => <option key={g.name} value={g.name} />)}</datalist>
            </div>
            <div style={{ flex: 1, minWidth: "110px" }}>
              <label style={s.label}>Beilage</label>
              <input style={{ ...s.input, width: "100%" }} list="dl-b-neu" value={neueBeilage} onChange={(e) => setNeueBeilage(e.target.value)} placeholder="Beilage..." />
              <datalist id="dl-b-neu">{beilagenOptions.map((b) => <option key={b} value={b} />)}</datalist>
            </div>
            <div style={{ flex: 1, minWidth: "100px" }}>
              <label style={s.label}>Dessert</label>
              <input style={{ ...s.input, width: "100%" }} list="dl-d-neu" value={neuerDessert} onChange={(e) => setNeuerDessert(e.target.value)} placeholder="Dessert..." />
              <datalist id="dl-d-neu">{DESSERTS.map((d) => <option key={d} value={d} />)}</datalist>
            </div>
            <div style={{ flex: 2, minWidth: "130px" }}>
              <label style={s.label}>Bemerkung</label>
              <input style={{ ...s.input, width: "100%" }} value={neueBemerkung} onChange={(e) => setNeueBemerkung(e.target.value)} placeholder="Bemerkung..." />
            </div>
          </div>
          <button type="submit" style={s.btn}>+ Hinzufügen</button>
        </form>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={plan.map((p) => p.id)} strategy={verticalListSortingStrategy}>
          {plan.map((eintrag, idx) => (
            <div key={eintrag.id}>
              <SortableEintrag
                eintrag={eintrag}
                alleGerichte={gerichte}
                hatDatum={hatDatum}
                onAendern={(data) => eintragAendern(eintrag.id, data)}
                onLoeschen={() => entfernen(eintrag.id)}
              />
              <div style={{ display: "flex", justifyContent: eintrag.istUnterEintrag ? "flex-start" : "center", paddingLeft: eintrag.istUnterEintrag ? "2rem" : "0", marginBottom: "0.1rem" }}>
                <button
                  onClick={() => zwischenEinfuegen(idx)}
                  title="Ergänzungsgericht darunter einfügen"
                  style={{ background: "none", border: "1px dashed #c0d4e8", borderRadius: "4px", cursor: "pointer", color: "#7aa8cc", fontSize: "0.75rem", padding: "0.1rem 0.7rem", lineHeight: 1.4, transition: "all 0.15s" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#eaf3ff"; e.currentTarget.style.color = "#1a73e8"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#7aa8cc"; }}
                >+ Ergänzung</button>
              </div>
            </div>
          ))}
        </SortableContext>
      </DndContext>
      {plan.length === 0 && <p style={{ color: "#999", textAlign: "center" }}>Noch kein Speiseplan.</p>}

      {/* ── Küchen-Review ── */}
      <div style={{ borderTop: "2px solid #e0e8f0", marginTop: "1.5rem", paddingTop: "1.2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
          <div style={{ fontWeight: 700, color: "#1a3558", fontSize: "0.95rem" }}>📋 Küchen-Review</div>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            {!reviewGespeichert && <span style={{ fontSize: "0.75rem", color: "#e67700" }}>● ungespeichert</span>}
            <button style={s.btnGreen} onClick={reviewSpeichern} disabled={reviewGespeichert}>💾 Speichern</button>
            <button
              style={{ ...s.btnSm, fontSize: "0.75rem" }}
              onClick={() => { setReview((v) => v + (v && !v.endsWith("\n") ? "\n" : "") + "• "); setTimeout(() => { const ta = document.getElementById("kuechenReviewTA"); if (ta) { ta.focus(); ta.selectionStart = ta.selectionEnd = ta.value.length; } }, 0); }}
              title="Bulletpoint einfügen"
            >• Bullet</button>
          </div>
        </div>
        <textarea
          id="kuechenReviewTA"
          value={review}
          onChange={(e) => { setReview(e.target.value); setReviewGespeichert(false); }}
          onKeyDown={reviewKeyDown}
          onBlur={reviewGespeichert ? undefined : reviewSpeichern}
          placeholder={"Notizen zur Küche, Hinweise, Besonderheiten...\n• Bullet mit Enter weiterführen\n• Leerer Bullet → Enter beendet Liste"}
          style={{ width: "100%", minHeight: "160px", padding: "0.75rem", fontSize: "0.9rem", fontFamily: "sans-serif", lineHeight: 1.6, border: "1px solid #c5d8ec", borderRadius: "8px", resize: "vertical", outline: "none", boxSizing: "border-box", backgroundColor: reviewGespeichert ? "white" : "#fffbf0" }}
        />
      </div>
    </div>
  );
}

// ── Einkaufsliste-Tab ──────────────────────────────────────────────────────────
function EinkaufslisteTab({ lager, artikel, uid, onUpdate }) {
  const [liste, setListe] = useState(lager.einkaufsliste || []);
  const [suchText, setSuchText] = useState("");
  const [dropOffen, setDropOffen] = useState(false);
  const [aktiveTab, setAktiveTab] = useState("liste"); // "liste" | "referenz"
  // Referenzliste
  const [refSuche, setRefSuche] = useState("");
  const [editRefId, setEditRefId] = useState(null);
  const [editRefData, setEditRefData] = useState({});
  const [neuerRefModus, setNeuerRefModus] = useState(false);
  const [neuerRef, setNeuerRef] = useState({ name: "", gruppe: "Sonstiges", mengeProEinheit: "", bemerkung: "" });

  const listeUpdaten = async (neu) => {
    setListe(neu);
    await updateDoc(doc(db, "ferienlager", lager.id), { einkaufsliste: neu });
    onUpdate({ ...lager, einkaufsliste: neu });
  };

  // Autocomplete-Dropdown: filtert Referenzliste nach Eingabe
  const treffer = suchText.trim().length > 0
    ? artikel.filter((a) => a.name.toLowerCase().includes(suchText.toLowerCase())).slice(0, 10)
    : [];

  const artikelAuswaehlen = async (a) => {
    setSuchText(""); setDropOffen(false);
    if (liste.some((i) => i.name.toLowerCase() === a.name.toLowerCase())) return;
    await listeUpdaten([...liste, {
      id: Date.now().toString(), name: a.name, gruppe: a.gruppe || "Sonstiges",
      mengeProEinheit: a.mengeProEinheit || "", anzahl: "1", erledigt: false,
    }]);
  };

  // Freier Eintrag (nicht in Referenz vorhanden): Enter oder Button
  const freiHinzufuegen = async () => {
    const name = suchText.trim();
    if (!name) return;
    setSuchText(""); setDropOffen(false);
    if (liste.some((i) => i.name.toLowerCase() === name.toLowerCase())) return;
    // In Referenzliste speichern falls neu
    let gruppe = "Sonstiges"; let mengeProEinheit = "";
    const vorhandener = artikel.find((a) => a.name.toLowerCase() === name.toLowerCase());
    if (vorhandener) { gruppe = vorhandener.gruppe; mengeProEinheit = vorhandener.mengeProEinheit || ""; }
    else { await addDoc(collection(db, "ferienlager_artikel"), { name, gruppe, mengeProEinheit, uid }); }
    await listeUpdaten([...liste, { id: Date.now().toString(), name, gruppe, mengeProEinheit, anzahl: "1", erledigt: false }]);
  };

  const toggleErledigt = (id) => listeUpdaten(liste.map((i) => i.id === id ? { ...i, erledigt: !i.erledigt } : i));
  const loeschen = (id) => listeUpdaten(liste.filter((i) => i.id !== id));
  const anzahlAendern = (id, val) => listeUpdaten(liste.map((i) => i.id === id ? { ...i, anzahl: val } : i));
  const listeLeeren = () => listeUpdaten([]);

  // Referenzliste-Verwaltung
  const refSpeichern = async (e) => {
    e.preventDefault();
    if (!neuerRef.name.trim()) return;
    await addDoc(collection(db, "ferienlager_artikel"), { ...neuerRef, uid });
    setNeuerRef({ name: "", gruppe: "Sonstiges", mengeProEinheit: "" });
    setNeuerRefModus(false);
  };
  const refEditSpeichern = async () => {
    await updateDoc(doc(db, "ferienlager_artikel", editRefId), editRefData);
    setEditRefId(null);
  };

  // Gruppierung der Einkaufsliste
  const nachGruppe = {};
  liste.forEach((item) => { const g = item.gruppe || "Sonstiges"; if (!nachGruppe[g]) nachGruppe[g] = []; nachGruppe[g].push(item); });
  const offen = liste.filter((i) => !i.erledigt).length;

  // Referenzliste gefiltert
  const refGefiltert = artikel.filter((a) => a.name.toLowerCase().includes(refSuche.toLowerCase()));
  const refNachGruppe = {};
  refGefiltert.forEach((a) => { if (!refNachGruppe[a.gruppe]) refNachGruppe[a.gruppe] = []; refNachGruppe[a.gruppe].push(a); });

  return (
    <div>
      {/* ── Sub-Tabs ── */}
      <div style={{ display: "flex", gap: "0.4rem", marginBottom: "1rem" }}>
        {[["liste", "🛒 Einkaufsliste"], ["referenz", "📋 Zutaten-Referenz"]].map(([id, label]) => (
          <button key={id} style={{ ...s.tab, ...(aktiveTab === id ? s.tabAktiv : {}) }} onClick={() => setAktiveTab(id)}>{label}</button>
        ))}
      </div>

      {aktiveTab === "liste" && (
        <div>
          {/* ── Eingabefeld mit Autocomplete ── */}
          <div style={{ ...s.card, position: "relative" }}>
            <div style={{ fontWeight: 600, color: "#1a3558", marginBottom: "0.6rem", fontSize: "0.95rem" }}>Zutat hinzufügen</div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <div style={{ position: "relative", flex: 1 }}>
                <input
                  style={{ ...s.input, width: "100%", fontSize: "1rem", padding: "0.6rem 0.8rem" }}
                  placeholder="Zutat eingeben..."
                  value={suchText}
                  onChange={(e) => { setSuchText(e.target.value); setDropOffen(true); }}
                  onFocus={() => setDropOffen(true)}
                  onBlur={() => setTimeout(() => setDropOffen(false), 180)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (treffer.length > 0) artikelAuswaehlen(treffer[0]); else freiHinzufuegen(); } }}
                />
                {dropOffen && treffer.length > 0 && (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 300, backgroundColor: "white", border: "1px solid #ccc", borderRadius: "0 0 8px 8px", boxShadow: "0 6px 20px rgba(0,0,0,0.15)", maxHeight: "260px", overflowY: "auto" }}>
                    {treffer.map((a) => (
                      <div
                        key={a.id}
                        onMouseDown={() => artikelAuswaehlen(a)}
                        style={{ padding: "0.55rem 1rem", cursor: "pointer", borderBottom: "1px solid #f5f5f5", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                      >
                        <span style={{ fontWeight: 500 }}>{a.name}</span>
                        <span style={{ fontSize: "0.78rem", color: "#888" }}>{a.mengeProEinheit && `${a.mengeProEinheit} · `}{a.gruppe}</span>
                      </div>
                    ))}
                    {suchText.trim() && !artikel.some((a) => a.name.toLowerCase() === suchText.toLowerCase()) && (
                      <div onMouseDown={freiHinzufuegen} style={{ padding: "0.55rem 1rem", cursor: "pointer", color: "#1a73e8", fontStyle: "italic", borderTop: "1px solid #e0e0e0" }}>
                        „{suchText}" als neuen Eintrag hinzufügen
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button style={s.btn} onMouseDown={freiHinzufuegen}>+ Hinzufügen</button>
            </div>
          </div>

          {/* ── Liste ── */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "0.7rem 0 0.4rem" }}>
            <span style={{ fontWeight: 600, color: "#1a3558" }}>
              {offen} offen <span style={{ fontWeight: 400, color: "#aaa" }}>· {liste.length - offen} erledigt</span>
            </span>
            {liste.length > 0 && <button style={{ ...s.btnSm, color: "#ea4335" }} onClick={listeLeeren}>Liste leeren</button>}
          </div>

          {liste.length === 0 && <p style={{ color: "#bbb", textAlign: "center", marginTop: "2rem", fontSize: "0.9rem" }}>Liste ist leer. Oben Zutat eingeben.</p>}

          {[...PRODUKTGRUPPEN, ...Object.keys(nachGruppe).filter((g) => !PRODUKTGRUPPEN.includes(g))].filter((g) => nachGruppe[g]?.length > 0).map((gruppe) => (
            <div key={gruppe} style={{ marginBottom: "0.6rem", backgroundColor: "white", borderRadius: "8px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.07)" }}>
              <div style={s.gruppeTitle}>{gruppe}</div>
              {nachGruppe[gruppe].map((item) => (
                <div key={item.id} style={{ display: "flex", gap: "0.5rem", alignItems: "center", padding: "0.45rem 0.8rem", borderBottom: "1px solid #f5f5f5", backgroundColor: item.erledigt ? "#fafafa" : "white" }}>
                  <input type="checkbox" checked={item.erledigt || false} onChange={() => toggleErledigt(item.id)} style={{ cursor: "pointer", width: "16px", height: "16px" }} />
                  <span style={{ flex: 1, textDecoration: item.erledigt ? "line-through" : "none", color: item.erledigt ? "#bbb" : "#1a3558", fontWeight: 500, fontSize: "0.9rem" }}>{item.name}</span>
                  {item.mengeProEinheit && <span style={{ fontSize: "0.75rem", color: "#999", whiteSpace: "nowrap" }}>{item.mengeProEinheit}</span>}
                  <span style={{ fontSize: "0.78rem", color: "#aaa" }}>×</span>
                  <input
                    style={{ ...s.input, width: "52px", padding: "0.25rem 0.3rem", textAlign: "center", fontSize: "0.9rem" }}
                    type="number" min="1" value={item.anzahl || "1"}
                    onChange={(e) => anzahlAendern(item.id, e.target.value)}
                  />
                  <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1rem", color: "#ccc", lineHeight: 1 }} onClick={() => loeschen(item.id)}>✕</button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {aktiveTab === "referenz" && (
        <div>
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.8rem" }}>
            <input style={{ ...s.input, flex: 1 }} placeholder="Suchen..." value={refSuche} onChange={(e) => setRefSuche(e.target.value)} />
            <button style={s.btn} onClick={() => setNeuerRefModus(!neuerRefModus)}>+ Neue Zutat</button>
          </div>

          {neuerRefModus && (
            <div style={{ ...s.card, border: "1px solid #1a73e8" }}>
              <form onSubmit={refSpeichern} style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "flex-end" }}>
                <div style={{ flex: 2, minWidth: "130px" }}>
                  <label style={s.label}>Name</label>
                  <input style={{ ...s.input, width: "100%" }} placeholder="Zutat..." value={neuerRef.name} onChange={(e) => setNeuerRef({ ...neuerRef, name: e.target.value })} autoFocus />
                </div>
                <div style={{ flex: 1, minWidth: "120px" }}>
                  <label style={s.label}>Kategorie</label>
                  <input style={{ ...s.input, width: "100%" }} list="dl-gruppen-ref-neu" placeholder="Kategorie..." value={neuerRef.gruppe} onChange={(e) => setNeuerRef({ ...neuerRef, gruppe: e.target.value })} />
                  <datalist id="dl-gruppen-ref-neu">{PRODUKTGRUPPEN.map((g) => <option key={g} value={g} />)}</datalist>
                </div>
                <div style={{ flex: 1, minWidth: "110px" }}>
                  <label style={s.label}>Menge/Gebinde</label>
                  <input style={{ ...s.input, width: "100%" }} placeholder="z.B. 500g, 6er Pack" value={neuerRef.mengeProEinheit} onChange={(e) => setNeuerRef({ ...neuerRef, mengeProEinheit: e.target.value })} />
                </div>
                <div style={{ flex: 2, minWidth: "150px" }}>
                  <label style={s.label}>Bemerkung</label>
                  <input style={{ ...s.input, width: "100%" }} placeholder="Hinweis, Marke, Info..." value={neuerRef.bemerkung || ""} onChange={(e) => setNeuerRef({ ...neuerRef, bemerkung: e.target.value })} />
                </div>
                <button type="submit" style={s.btnGreen}>Speichern</button>
                <button type="button" style={s.btnSm} onClick={() => setNeuerRefModus(false)}>Abbrechen</button>
              </form>
            </div>
          )}

          <div style={{ fontSize: "0.8rem", color: "#999", marginBottom: "0.5rem" }}>{refGefiltert.length} Einträge</div>

          {/* Alle Gruppen: bekannte zuerst, danach freie Gruppen die nicht in PRODUKTGRUPPEN sind */}
          {[...PRODUKTGRUPPEN, ...Object.keys(refNachGruppe).filter((g) => !PRODUKTGRUPPEN.includes(g))].filter((g) => refNachGruppe[g]?.length > 0).map((gruppe) => (
            <div key={gruppe} style={{ marginBottom: "0.6rem", backgroundColor: "white", borderRadius: "8px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.07)" }}>
              <div style={s.gruppeTitle}>{gruppe}</div>
              {refNachGruppe[gruppe].map((a) => (
                <div key={a.id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                  {editRefId === a.id ? (
                    <div style={{ display: "flex", gap: "0.4rem", padding: "0.5rem 0.7rem", flexWrap: "wrap", alignItems: "center", backgroundColor: "#f8fbff" }}>
                      <input style={{ ...s.input, flex: 2, minWidth: "110px" }} value={editRefData.name || ""} onChange={(e) => setEditRefData({ ...editRefData, name: e.target.value })} />
                      <input style={{ ...s.input, width: "130px" }} list="dl-gruppen-ref-edit" placeholder="Kategorie..." value={editRefData.gruppe || ""} onChange={(e) => setEditRefData({ ...editRefData, gruppe: e.target.value })} />
                      <datalist id="dl-gruppen-ref-edit">{PRODUKTGRUPPEN.map((g) => <option key={g} value={g} />)}</datalist>
                      <input style={{ ...s.input, width: "110px" }} placeholder="Gebinde" value={editRefData.mengeProEinheit || ""} onChange={(e) => setEditRefData({ ...editRefData, mengeProEinheit: e.target.value })} />
                      <input style={{ ...s.input, flex: 1, minWidth: "120px" }} placeholder="Bemerkung..." value={editRefData.bemerkung || ""} onChange={(e) => setEditRefData({ ...editRefData, bemerkung: e.target.value })} />
                      <button style={s.btnGreen} onClick={refEditSpeichern}>💾</button>
                      <button style={s.btnSm} onClick={() => setEditRefId(null)}>✕</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", padding: "0.4rem 0.8rem" }}>
                      <span style={{ flex: 1, fontSize: "0.88rem", fontWeight: 500, color: "#1a3558" }}>{a.name}</span>
                      <span style={{ fontSize: "0.78rem", color: "#888", minWidth: "80px" }}>{a.mengeProEinheit || "—"}</span>
                      {a.bemerkung && <span style={{ fontSize: "0.76rem", color: "#aaa", fontStyle: "italic", flex: 1 }}>{a.bemerkung}</span>}
                      <button style={s.btnSm} onClick={() => { setEditRefId(a.id); setEditRefData({ ...a }); }}>✏️</button>
                      <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.9rem", color: "#ccc" }} onClick={() => deleteDoc(doc(db, "ferienlager_artikel", a.id))}>✕</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}

          {refGefiltert.length === 0 && <p style={{ color: "#bbb", textAlign: "center", marginTop: "2rem" }}>Keine Einträge. Oben neue Zutat anlegen.</p>}
        </div>
      )}
    </div>
  );
}

// ── Budget-Tab ─────────────────────────────────────────────────────────────────
function BudgetTab({ lager, onUpdate }) {
  const [budget, setBudget] = useState(lager.budget || 0);
  const [budgetEdit, setBudgetEdit] = useState(String(lager.budget || 0));
  const [ausgaben, setAusgaben] = useState(lager.ausgaben || []);
  const [neueKat, setNeueKat] = useState("Lebensmittel");
  const [neueBeschr, setNeueBeschr] = useState("");
  const [neuePlan, setNeuePlan] = useState("");
  const [neueIst, setNeueIst] = useState("");
  const fmt = (n) => n.toLocaleString("de-DE", { style: "currency", currency: "EUR" });

  const budgetSpeichern = async () => {
    const val = parseFloat(budgetEdit) || 0; setBudget(val);
    await updateDoc(doc(db, "ferienlager", lager.id), { budget: val });
    onUpdate({ ...lager, budget: val });
  };

  const ausgabeHinzufuegen = async (e) => {
    e.preventDefault();
    if (!neueBeschr.trim()) return;
    const eintrag = { id: Date.now().toString(), kategorie: neueKat, beschreibung: neueBeschr, geplant: parseFloat(neuePlan) || 0, ist: parseFloat(neueIst) || 0 };
    const neu = [...ausgaben, eintrag]; setAusgaben(neu);
    await updateDoc(doc(db, "ferienlager", lager.id), { ausgaben: neu });
    onUpdate({ ...lager, ausgaben: neu });
    setNeueBeschr(""); setNeuePlan(""); setNeueIst("");
  };

  const ausgabeAktualisieren = async (id, feld, wert) => {
    const neu = ausgaben.map((a) => a.id === id ? { ...a, [feld]: feld === "beschreibung" || feld === "kategorie" ? wert : parseFloat(wert) || 0 } : a);
    setAusgaben(neu);
    await updateDoc(doc(db, "ferienlager", lager.id), { ausgaben: neu });
  };

  const ausgabeLoeschen = async (id) => {
    const neu = ausgaben.filter((a) => a.id !== id); setAusgaben(neu);
    await updateDoc(doc(db, "ferienlager", lager.id), { ausgaben: neu });
    onUpdate({ ...lager, ausgaben: neu });
  };

  const gesamtGeplant = ausgaben.reduce((sm, a) => sm + (a.geplant || 0), 0);
  const gesamtIst = ausgaben.reduce((sm, a) => sm + (a.ist || 0), 0);
  const rest = budget - gesamtIst;

  return (
    <div>
      <div style={s.budget}>
        {[["Budget", budget, "#1a73e8", "#e8f0fe"], ["Geplant", gesamtGeplant, "#f9a825", "#fff8e1"], ["Ausgegeben", gesamtIst, "#c2185b", "#fce8f3"], ["Verbleibend", rest, rest >= 0 ? "#34a853" : "#ea4335", rest >= 0 ? "#e6f4ea" : "#fce8f3"]].map(([label, val, color, bg]) => (
          <div key={label} style={{ ...s.budgetBox, backgroundColor: bg }}>
            <div style={{ fontSize: "0.8rem", color: "#666" }}>{label}</div>
            <div style={{ fontWeight: "700", fontSize: "1.3rem", color }}>{fmt(val)}</div>
          </div>
        ))}
      </div>
      <div style={s.card}>
        <div style={s.row}>
          <label style={{ ...s.label, marginBottom: 0 }}>Budget (€):</label>
          <input style={{ ...s.input, width: "120px" }} type="number" value={budgetEdit} onChange={(e) => setBudgetEdit(e.target.value)} />
          <button style={s.btn} onClick={budgetSpeichern}>Speichern</button>
        </div>
      </div>
      <div style={s.card}>
        <h3 style={s.h3}>Ausgabe hinzufügen</h3>
        <form onSubmit={ausgabeHinzufuegen} style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <select style={s.select} value={neueKat} onChange={(e) => setNeueKat(e.target.value)}>{AUSGABEN_KATEGORIEN.map((k) => <option key={k}>{k}</option>)}</select>
          <input style={{ ...s.input, flex: 1, minWidth: "120px" }} placeholder="Beschreibung..." value={neueBeschr} onChange={(e) => setNeueBeschr(e.target.value)} />
          <input style={{ ...s.input, width: "90px" }} type="number" placeholder="Geplant €" value={neuePlan} onChange={(e) => setNeuePlan(e.target.value)} />
          <input style={{ ...s.input, width: "90px" }} type="number" placeholder="Ist €" value={neueIst} onChange={(e) => setNeueIst(e.target.value)} />
          <button type="submit" style={s.btn}>+ Hinzufügen</button>
        </form>
      </div>
      {ausgaben.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem", backgroundColor: "white", borderRadius: "8px", overflow: "hidden" }}>
            <thead><tr style={{ backgroundColor: "#f0f2f5" }}>
              {["Kategorie", "Beschreibung", "Geplant", "Ist", "Diff.", ""].map((h) => <th key={h} style={{ padding: "0.6rem", textAlign: h === "Geplant" || h === "Ist" || h === "Diff." ? "right" : "left" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {ausgaben.map((a) => {
                const diff = (a.geplant || 0) - (a.ist || 0);
                return (
                  <tr key={a.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "0.5rem" }}><select style={{ ...s.select, padding: "0.2rem" }} value={a.kategorie} onChange={(e) => ausgabeAktualisieren(a.id, "kategorie", e.target.value)}>{AUSGABEN_KATEGORIEN.map((k) => <option key={k}>{k}</option>)}</select></td>
                    <td style={{ padding: "0.5rem" }}><input style={{ ...s.input, width: "100%", padding: "0.2rem 0.4rem" }} value={a.beschreibung} onChange={(e) => ausgabeAktualisieren(a.id, "beschreibung", e.target.value)} /></td>
                    <td style={{ padding: "0.5rem", textAlign: "right" }}><input style={{ ...s.input, width: "80px", textAlign: "right", padding: "0.2rem 0.4rem" }} type="number" value={a.geplant || 0} onChange={(e) => ausgabeAktualisieren(a.id, "geplant", e.target.value)} /></td>
                    <td style={{ padding: "0.5rem", textAlign: "right" }}><input style={{ ...s.input, width: "80px", textAlign: "right", padding: "0.2rem 0.4rem" }} type="number" value={a.ist || 0} onChange={(e) => ausgabeAktualisieren(a.id, "ist", e.target.value)} /></td>
                    <td style={{ padding: "0.5rem", textAlign: "right", color: diff >= 0 ? "#34a853" : "#ea4335", fontWeight: "600" }}>{fmt(diff)}</td>
                    <td style={{ padding: "0.5rem" }}><button style={s.btnDanger} onClick={() => ausgabeLoeschen(a.id)}>🗑️</button></td>
                  </tr>
                );
              })}
              <tr style={{ fontWeight: "700", backgroundColor: "#f8f9fa" }}>
                <td colSpan={2} style={{ padding: "0.6rem" }}>Gesamt</td>
                <td style={{ padding: "0.6rem", textAlign: "right" }}>{fmt(gesamtGeplant)}</td>
                <td style={{ padding: "0.6rem", textAlign: "right" }}>{fmt(gesamtIst)}</td>
                <td style={{ padding: "0.6rem", textAlign: "right", color: (gesamtGeplant - gesamtIst) >= 0 ? "#34a853" : "#ea4335" }}>{fmt(gesamtGeplant - gesamtIst)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── ToDo-Tab ───────────────────────────────────────────────────────────────────
function TodoTab({ lager, onUpdate }) {
  const [todos, setTodos] = useState(lager.todos || []);
  const [neuerText, setNeuerText] = useState("");

  const hinzufuegen = async (e) => {
    e.preventDefault();
    if (!neuerText.trim()) return;
    const neu = [...todos, { id: Date.now().toString(), text: neuerText, erledigt: false }];
    setTodos(neu);
    await updateDoc(doc(db, "ferienlager", lager.id), { todos: neu });
    onUpdate({ ...lager, todos: neu });
    setNeuerText("");
  };
  const toggle = async (id) => { const neu = todos.map((t) => t.id === id ? { ...t, erledigt: !t.erledigt } : t); setTodos(neu); await updateDoc(doc(db, "ferienlager", lager.id), { todos: neu }); };
  const loeschen = async (id) => { const neu = todos.filter((t) => t.id !== id); setTodos(neu); await updateDoc(doc(db, "ferienlager", lager.id), { todos: neu }); onUpdate({ ...lager, todos: neu }); };

  return (
    <div>
      <div style={s.card}>
        <form onSubmit={hinzufuegen} style={{ display: "flex", gap: "0.5rem" }}>
          <input style={{ ...s.input, flex: 1 }} placeholder="Neue Aufgabe..." value={neuerText} onChange={(e) => setNeuerText(e.target.value)} />
          <button type="submit" style={s.btn}>+ Hinzufügen</button>
        </form>
      </div>
      <div style={{ fontSize: "0.8rem", color: "#888", marginBottom: "0.5rem" }}>{todos.filter((t) => !t.erledigt).length} offen · {todos.filter((t) => t.erledigt).length} erledigt</div>
      {todos.map((todo) => (
        <div key={todo.id} style={{ ...s.card, display: "flex", gap: "0.7rem", alignItems: "center", padding: "0.7rem 1rem" }}>
          <input type="checkbox" checked={todo.erledigt || false} onChange={() => toggle(todo.id)} style={{ cursor: "pointer" }} />
          <span style={{ flex: 1, textDecoration: todo.erledigt ? "line-through" : "none", color: todo.erledigt ? "#aaa" : "inherit" }}>{todo.text}</span>
          <button style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => loeschen(todo.id)}>🗑️</button>
        </div>
      ))}
    </div>
  );
}

// ── Küchenartikel-Tab (Packliste: mitnehmen / nicht mitnehmen) ─────────────────
function KuechenartikelTab({ lager, onUpdate }) {
  const [artikel, setArtikel] = useState(lager.kuechenartikel || []);
  const [neuerName, setNeuerName] = useState("");

  const speichern = async (neu) => {
    setArtikel(neu);
    await updateDoc(doc(db, "ferienlager", lager.id), { kuechenartikel: neu });
    onUpdate({ ...lager, kuechenartikel: neu });
  };

  const hinzufuegen = (e) => {
    e.preventDefault();
    if (!neuerName.trim()) return;
    speichern([...artikel, { id: Date.now().toString(), name: neuerName, mitnehmen: true }]);
    setNeuerName("");
  };
  const toggle = (id) => speichern(artikel.map((a) => a.id === id ? { ...a, mitnehmen: !a.mitnehmen } : a));
  const loeschen = (id) => speichern(artikel.filter((a) => a.id !== id));

  const mitnehmen = artikel.filter((a) => a.mitnehmen);
  const nichtMitnehmen = artikel.filter((a) => !a.mitnehmen);

  return (
    <div>
      <div style={s.card}>
        <form onSubmit={hinzufuegen} style={{ display: "flex", gap: "0.5rem" }}>
          <input style={{ ...s.input, flex: 1 }} placeholder="Küchenartikel (z.B. großer Kochtopf, Schneidebretter …)" value={neuerName} onChange={(e) => setNeuerName(e.target.value)} />
          <button type="submit" style={s.btn}>+ Hinzufügen</button>
        </form>
      </div>
      <div style={{ fontSize: "0.8rem", color: "#888", marginBottom: "0.5rem" }}>{mitnehmen.length} mitnehmen · {nichtMitnehmen.length} nicht mitnehmen</div>
      {artikel.map((a) => (
        <div key={a.id} style={{ ...s.card, display: "flex", gap: "0.7rem", alignItems: "center", padding: "0.7rem 1rem" }}>
          <span style={{ flex: 1, color: a.mitnehmen ? "inherit" : "#aaa", textDecoration: a.mitnehmen ? "none" : "line-through" }}>{a.name}</span>
          <span style={{ ...s.tag, backgroundColor: a.mitnehmen ? "#e6f4ea" : "#fdecea", color: a.mitnehmen ? "#34a853" : "#c0392b" }}>{a.mitnehmen ? "Mitnehmen" : "Nicht mitnehmen"}</span>
          <button style={s.btnSm} onClick={() => toggle(a.id)}>Umschalten</button>
          <button style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => loeschen(a.id)}>🗑️</button>
        </div>
      ))}
      {artikel.length === 0 && <p style={{ color: "#999", textAlign: "center" }}>Noch keine Küchenartikel erfasst.</p>}
    </div>
  );
}

// ── Lebensmittel-vorhanden-Tab (Bestand, der ins nächste Lager mitgenommen werden kann) ──
function LebensmittelVorhandenTab({ lager, onUpdate }) {
  const [liste, setListe] = useState(lager.lebensmittelVorhanden || []);
  const [neuerName, setNeuerName] = useState("");
  const [neueMenge, setNeueMenge] = useState("");

  const speichern = async (neu) => {
    setListe(neu);
    await updateDoc(doc(db, "ferienlager", lager.id), { lebensmittelVorhanden: neu });
    onUpdate({ ...lager, lebensmittelVorhanden: neu });
  };

  const hinzufuegen = (e) => {
    e.preventDefault();
    if (!neuerName.trim()) return;
    speichern([...liste, { id: Date.now().toString(), name: neuerName, menge: neueMenge }]);
    setNeuerName(""); setNeueMenge("");
  };
  const loeschen = (id) => speichern(liste.filter((l) => l.id !== id));

  return (
    <div>
      <p style={{ fontSize: "0.8rem", color: "#888", marginBottom: "0.8rem" }}>
        Lebensmittel, die noch vorhanden sind (z.B. Reste aus dem letzten Lager) – hilft beim Einkauf, nichts doppelt zu kaufen.
      </p>
      <div style={s.card}>
        <form onSubmit={hinzufuegen} style={{ display: "flex", gap: "0.5rem" }}>
          <input style={{ ...s.input, flex: 2 }} placeholder="Lebensmittel..." value={neuerName} onChange={(e) => setNeuerName(e.target.value)} />
          <input style={{ ...s.input, flex: 1 }} placeholder="Menge (z.B. 5kg)" value={neueMenge} onChange={(e) => setNeueMenge(e.target.value)} />
          <button type="submit" style={s.btn}>+ Hinzufügen</button>
        </form>
      </div>
      {liste.map((l) => (
        <div key={l.id} style={{ ...s.card, display: "flex", gap: "0.7rem", alignItems: "center", padding: "0.7rem 1rem" }}>
          <span style={{ flex: 1 }}>{l.name}</span>
          {l.menge && <span style={s.tag}>{l.menge}</span>}
          <button style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => loeschen(l.id)}>🗑️</button>
        </div>
      ))}
      {liste.length === 0 && <p style={{ color: "#999", textAlign: "center" }}>Noch keine vorhandenen Lebensmittel erfasst.</p>}
    </div>
  );
}

// ── Gerichte-Tab ───────────────────────────────────────────────────────────────
function GerichteTab({ gerichte, artikel, user, lager, onUpdate }) {
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [neuerModus, setNeuerModus] = useState(false);
  const [neuesGericht, setNeuesGericht] = useState({ name: "", beilagen: "", zutaten: [{ name: "", mengeProEinheit: "", anzahl: "1", gruppe: "Sonstiges" }] });
  const [suche, setSuche] = useState("");

  const zutatInKatalog = async (zutaten) => {
    for (const z of zutaten) {
      if (!z.name.trim()) continue;
      if (!artikel.find((a) => a.name.toLowerCase() === z.name.toLowerCase())) {
        await addDoc(collection(db, "ferienlager_artikel"), { name: z.name, gruppe: z.gruppe || "Sonstiges", mengeProEinheit: z.mengeProEinheit || "", uid: user.uid });
      }
    }
  };

  const editStart = (g) => {
    setEditId(g.id);
    setEditData({ ...g, beilagenText: (g.beilagen || []).join(", "), zutaten: (g.zutaten || []).map((z) => ({ name: z.name || "", mengeProEinheit: z.mengeProEinheit || z.menge || "", anzahl: z.anzahl || "1", gruppe: z.gruppe || "Sonstiges" })) });
  };

  const editSpeichern = async () => {
    const beilagen = editData.beilagenText.split(",").map((b) => b.trim()).filter(Boolean);
    const zutaten = editData.zutaten.filter((z) => z.name.trim());
    await zutatInKatalog(zutaten);
    await updateDoc(doc(db, "ferienlager_gerichte", editId), { name: editData.name, beilagen, zutaten });
    setEditId(null);
  };

  const neuesSpeichern = async (e) => {
    e.preventDefault();
    if (!neuesGericht.name.trim()) return;
    const beilagen = neuesGericht.beilagen.split(",").map((b) => b.trim()).filter(Boolean);
    const zutaten = neuesGericht.zutaten.filter((z) => z.name.trim());
    await zutatInKatalog(zutaten);
    await addDoc(collection(db, "ferienlager_gerichte"), { name: neuesGericht.name, beilagen, zutaten, uid: user.uid, erstellt: new Date() });
    setNeuesGericht({ name: "", beilagen: "", zutaten: [{ name: "", mengeProEinheit: "", anzahl: "1", gruppe: "Sonstiges" }] });
    setNeuerModus(false);
  };

  const gerichtInEinkaufsliste = async (g) => {
    const neu = [...(lager.einkaufsliste || [])];
    const map = {};
    neu.filter((i) => !i.frei).forEach((i) => { map[i.name.toLowerCase()] = i; });
    (g.zutaten || []).forEach((z) => {
      const key = z.name.toLowerCase();
      if (!map[key]) map[key] = { id: Date.now().toString() + Math.random(), name: z.name, gruppe: z.gruppe || "Sonstiges", mengeProEinheit: z.mengeProEinheit || z.menge || "", anzahl: z.anzahl || "1", erledigt: false, frei: false };
    });
    const updated = [...Object.values(map), ...neu.filter((i) => i.frei)];
    await updateDoc(doc(db, "ferienlager", lager.id), { einkaufsliste: updated });
    onUpdate({ ...lager, einkaufsliste: updated });
  };

  const renderZutaten = (zutaten, onZutatChange, onZutatRemove, onZutatAdd) => (
    <div>
      {zutaten.map((z, i) => (
        <ZutatZeile key={i} zutat={z} artikel={artikel}
          onChange={(neu) => onZutatChange(i, neu)}
          onRemove={() => onZutatRemove(i)} />
      ))}
      <button type="button" style={s.btnSm} onClick={onZutatAdd}>+ Zutat</button>
    </div>
  );

  const gefiltert = gerichte.filter((g) => g.name.toLowerCase().includes(suche.toLowerCase()));

  return (
    <div>
      <div style={{ ...s.row, marginBottom: "1rem" }}>
        <input style={{ ...s.input, flex: 1 }} placeholder="Suchen..." value={suche} onChange={(e) => setSuche(e.target.value)} />
        <button style={s.btn} onClick={() => setNeuerModus(!neuerModus)}>+ Neues Gericht</button>
      </div>

      {neuerModus && (
        <div style={s.card}>
          <h3 style={s.h3}>Neues Gericht</h3>
          <form onSubmit={neuesSpeichern}>
            <label style={s.label}>Name</label>
            <input style={{ ...s.input, width: "100%", marginBottom: "0.5rem" }} value={neuesGericht.name} onChange={(e) => setNeuesGericht({ ...neuesGericht, name: e.target.value })} placeholder="Gerichtname..." />
            <label style={s.label}>Beilagen (kommagetrennt)</label>
            <input style={{ ...s.input, width: "100%", marginBottom: "0.8rem" }} value={neuesGericht.beilagen} onChange={(e) => setNeuesGericht({ ...neuesGericht, beilagen: e.target.value })} placeholder="z.B. Nudeln, Reis" />
            <label style={s.label}>Zutaten</label>
            {renderZutaten(
              neuesGericht.zutaten,
              (i, neu) => { const z = [...neuesGericht.zutaten]; z[i] = neu; setNeuesGericht({ ...neuesGericht, zutaten: z }); },
              (i) => setNeuesGericht({ ...neuesGericht, zutaten: neuesGericht.zutaten.filter((_, j) => j !== i) }),
              () => setNeuesGericht({ ...neuesGericht, zutaten: [...neuesGericht.zutaten, { name: "", mengeProEinheit: "", anzahl: "1", gruppe: "Sonstiges" }] })
            )}
            <div style={{ ...s.row, marginTop: "0.8rem" }}>
              <button type="submit" style={s.btn}>Speichern</button>
              <button type="button" style={s.btnSm} onClick={() => setNeuerModus(false)}>Abbrechen</button>
            </div>
          </form>
        </div>
      )}

      <div style={{ fontSize: "0.8rem", color: "#888", marginBottom: "0.5rem" }}>{gefiltert.length} Gerichte</div>

      {gefiltert.map((g) => (
        <div key={g.id} style={s.card}>
          {editId === g.id ? (
            <div>
              <input style={{ ...s.input, width: "100%", marginBottom: "0.5rem", fontWeight: "600" }} value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} />
              <label style={s.label}>Beilagen</label>
              <input style={{ ...s.input, width: "100%", marginBottom: "0.6rem" }} value={editData.beilagenText} onChange={(e) => setEditData({ ...editData, beilagenText: e.target.value })} />
              <label style={s.label}>Zutaten (Gebinde = Packungsgröße · Anz. = wie viele Packungen)</label>
              {renderZutaten(
                editData.zutaten,
                (i, neu) => { const z = [...editData.zutaten]; z[i] = neu; setEditData({ ...editData, zutaten: z }); },
                (i) => setEditData({ ...editData, zutaten: editData.zutaten.filter((_, j) => j !== i) }),
                () => setEditData({ ...editData, zutaten: [...editData.zutaten, { name: "", mengeProEinheit: "", anzahl: "1", gruppe: "Sonstiges" }] })
              )}
              <div style={{ ...s.row, marginTop: "0.8rem" }}>
                <button style={s.btnGreen} onClick={editSpeichern}>💾 Speichern</button>
                <button style={s.btnSm} onClick={() => setEditId(null)}>Abbrechen</button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: "600", color: "#1a3558" }}>{g.name}</div>
                {g.beilagen?.length > 0 && <div style={{ fontSize: "0.8rem", color: "#888" }}>Beilagen: {g.beilagen.join(", ")}</div>}
                {g.zutaten?.length > 0 && (
                  <div style={{ fontSize: "0.79rem", color: "#666", marginTop: "0.2rem" }}>
                    {g.zutaten.map((z) => `${z.name}${z.mengeProEinheit || z.menge ? ` (${z.mengeProEinheit || z.menge})` : ""}${z.anzahl && z.anzahl !== "1" ? ` ×${z.anzahl}` : ""}`).join(" · ")}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: "0.3rem", flexShrink: 0 }}>
                <button style={{ ...s.btnSm, backgroundColor: "#e8f0fe", color: "#1a73e8" }} title="Zutaten in Einkaufsliste" onClick={() => gerichtInEinkaufsliste(g)}>🛒</button>
                <button style={s.btnSm} onClick={() => editStart(g)}>✏️</button>
                {g.id && <button style={s.btnDanger} onClick={() => deleteDoc(doc(db, "ferienlager_gerichte", g.id))}>🗑️</button>}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Anmeldungen-Tab ───────────────────────────────────────────────────────────
const REGISTRIERUNG_ADMIN_DEFAULTS = {
  // depositPaid/remainderPaid werden nicht mehr angezeigt, müssen beim Anlegen aber laut
  // firestore.rules weiterhin mit false gesetzt sein.
  depositPaid: false, depositPaidDate: "", remainderPaid: false, remainderPaidDate: "",
  zahlungen: [],
  funding: "", illnesses: "", adminNotes: "", confirmed: false, group: "", miscAdmin: "",
};
const REGISTRIERUNG_LEER = {
  childFirstName: "", childLastName: "", childGender: "", childBirthdate: "", tshirtSize: "",
  guardianName: "", street: "", zip: "", city: "", phone: "", email: "", groupWishes: "", notes: "",
  ...REGISTRIERUNG_ADMIN_DEFAULTS,
};

function alterVonGeburtsdatum(datumStr) {
  if (!datumStr) return null;
  const b = new Date(datumStr);
  if (isNaN(b.getTime())) return null;
  const heute = new Date();
  let alter = heute.getFullYear() - b.getFullYear();
  const m = heute.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && heute.getDate() < b.getDate())) alter--;
  return alter;
}
function datumAnzeige(s2) {
  if (!s2) return "";
  const [y, m, d] = String(s2).split("-");
  if (!y || !m || !d) return s2;
  return `${d}.${m}.${y}`;
}
function euro(n) {
  return (n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}
function bezahltSumme(r) {
  return (r.zahlungen || []).reduce((a, z) => a + (Number(z.betrag) || 0), 0);
}
// Differenz = bezahlt − Beitrag (negativ = noch offen, positiv = Überzahlung); null ohne Beitrag
function differenz(r, beitrag) {
  return beitrag ? Math.round((bezahltSumme(r) - beitrag) * 100) / 100 : null;
}
function lokalesDatum(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function eingangMs(ts) {
  if (!ts) return 0;
  return (ts.toDate ? ts.toDate() : new Date(ts)).getTime();
}
function zeitstempelAnzeige(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("de-DE") + " " + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

function ZahlungenEditor({ zahlungen, beitrag, onChange }) {
  const liste = zahlungen || [];
  const aendern = (id, feld, wert) => onChange(liste.map((z) => (z.id === id ? { ...z, [feld]: feld === "betrag" ? (wert === "" ? "" : Number(wert)) : wert } : z)));
  const summe = liste.reduce((a, z) => a + (Number(z.betrag) || 0), 0);
  const diff = beitrag ? summe - beitrag : null;
  return (
    <div style={{ marginBottom: "0.6rem" }}>
      <label style={s.label}>Zahlungen</label>
      {liste.map((z) => (
        <div key={z.id} style={{ ...s.row, marginBottom: "0.3rem" }}>
          <input style={{ ...s.input, width: "140px" }} type="date" value={z.datum || ""} onChange={(e) => aendern(z.id, "datum", e.target.value)} />
          <input style={{ ...s.input, width: "90px", textAlign: "right" }} type="number" step="0.01" placeholder="€" value={z.betrag} onChange={(e) => aendern(z.id, "betrag", e.target.value)} />
          <input style={{ ...s.input, flex: 1, minWidth: "140px" }} placeholder="Notiz (z.B. Verwendungszweck)" value={z.notiz || ""} onChange={(e) => aendern(z.id, "notiz", e.target.value)} />
          <button type="button" style={s.btnSm} onClick={() => onChange(liste.filter((x) => x.id !== z.id))}>✕</button>
        </div>
      ))}
      <div style={s.row}>
        <button type="button" style={s.btnSm} onClick={() => onChange([...liste, { id: Date.now().toString(), datum: new Date().toISOString().slice(0, 10), betrag: "", notiz: "" }])}>+ Zahlung</button>
        <span style={{ fontSize: "0.82rem", color: "#555" }}>
          Bezahlt: <b>{euro(summe)}</b>{beitrag ? <> von {euro(beitrag)} · Differenz: <b style={{ color: diff < 0 ? "#c0392b" : "#2f7d54" }}>{euro(diff)}</b></> : " (kein Teilnehmerbeitrag festgelegt)"}
        </span>
      </div>
    </div>
  );
}

function AnmeldungFormular({ daten, onChange, beitrag }) {
  const set = (feld) => (e) => onChange({ ...daten, [feld]: e.target.value });
  const setChk = (feld) => (e) => onChange({ ...daten, [feld]: e.target.checked });
  return (
    <div>
      <div style={s.gruppeTitle}>Angaben zum Kind</div>
      <div style={s.row}>
        <div style={{ flex: 1 }}><label style={s.label}>Vorname *</label><input style={s.input} value={daten.childFirstName} onChange={set("childFirstName")} /></div>
        <div style={{ flex: 1 }}><label style={s.label}>Nachname *</label><input style={s.input} value={daten.childLastName} onChange={set("childLastName")} /></div>
      </div>
      <div style={s.row}>
        <div style={{ flex: 1 }}>
          <label style={s.label}>Geschlecht</label>
          <select style={s.select} value={daten.childGender} onChange={set("childGender")}>
            <option value="">–</option><option value="Junge">Junge</option><option value="Mädchen">Mädchen</option><option value="Divers">Divers</option>
          </select>
        </div>
        <div style={{ flex: 1 }}><label style={s.label}>Geburtsdatum</label><input style={s.input} type="date" value={daten.childBirthdate} onChange={set("childBirthdate")} /></div>
        <div style={{ flex: 1 }}>
          <label style={s.label}>T-Shirt-Größe</label>
          <select style={s.select} value={daten.tshirtSize} onChange={set("tshirtSize")}>
            <option value="">–</option>
            {["116", "128", "140", "152", "164", "176", "S", "M", "L", "XL"].map((g) => <option key={g}>{g}</option>)}
          </select>
        </div>
      </div>

      <div style={s.gruppeTitle}>Kontaktdaten</div>
      <div style={{ marginBottom: "0.6rem" }}><label style={s.label}>Erziehungsberechtigte</label><input style={s.input} value={daten.guardianName} onChange={set("guardianName")} /></div>
      <div style={s.row}>
        <div style={{ flex: 2 }}><label style={s.label}>Straße</label><input style={s.input} value={daten.street} onChange={set("street")} /></div>
        <div style={{ flex: 1 }}><label style={s.label}>PLZ</label><input style={s.input} value={daten.zip} onChange={set("zip")} /></div>
        <div style={{ flex: 1.5 }}><label style={s.label}>Ort</label><input style={s.input} value={daten.city} onChange={set("city")} /></div>
      </div>
      <div style={s.row}>
        <div style={{ flex: 1 }}><label style={s.label}>Telefon</label><input style={s.input} value={daten.phone} onChange={set("phone")} /></div>
        <div style={{ flex: 1 }}><label style={s.label}>E-Mail</label><input style={s.input} value={daten.email} onChange={set("email")} /></div>
      </div>
      <div style={{ marginBottom: "0.6rem" }}><label style={s.label}>Gruppenwünsche (Eltern)</label><textarea style={{ ...s.input, minHeight: "50px" }} value={daten.groupWishes} onChange={set("groupWishes")} /></div>
      <div style={{ marginBottom: "0.6rem" }}><label style={s.label}>Sonstiges (Eltern)</label><textarea style={{ ...s.input, minHeight: "50px" }} value={daten.notes} onChange={set("notes")} /></div>

      <div style={s.gruppeTitle}>Orga / Verwaltung</div>
      <div style={{ marginBottom: "0.6rem", maxWidth: "200px" }}>
        <label style={s.label}>Eingang der Anmeldung</label>
        <input style={s.input} type="date" value={daten.eingangDatum || ""} onChange={set("eingangDatum")} />
      </div>
      <ZahlungenEditor zahlungen={daten.zahlungen} beitrag={beitrag} onChange={(zahlungen) => onChange({ ...daten, zahlungen })} />
      <div style={{ marginBottom: "0.6rem" }}>
        <label style={s.checkboxRow}><input type="checkbox" checked={daten.confirmed} onChange={setChk("confirmed")} /> Anmeldung bestätigt</label>
      </div>
      <div style={s.row}>
        <div style={{ flex: 1 }}><label style={s.label}>Förderung</label><input style={s.input} value={daten.funding} onChange={set("funding")} placeholder="z.B. Bildung und Teilhabe" /></div>
        <div style={{ flex: 1 }}><label style={s.label}>Gruppe (final)</label><input style={s.input} value={daten.group} onChange={set("group")} /></div>
      </div>
      <div style={{ marginBottom: "0.6rem" }}><label style={s.label}>Krankheiten / Allergien / Medikamente</label><textarea style={{ ...s.input, minHeight: "50px" }} value={daten.illnesses} onChange={set("illnesses")} /></div>
      <div style={{ marginBottom: "0.6rem" }}><label style={s.label}>Sonstige Hinweise (Orga-intern)</label><textarea style={{ ...s.input, minHeight: "50px" }} value={daten.adminNotes} onChange={set("adminNotes")} /></div>
      <div style={{ marginBottom: "0.6rem" }}><label style={s.label}>Sonstiges (Orga-intern)</label><textarea style={{ ...s.input, minHeight: "50px" }} value={daten.miscAdmin} onChange={set("miscAdmin")} /></div>
    </div>
  );
}

function AnmeldungenTab({ lagerId, lagerName, kannAktivSetzen }) {
  const [anmeldungen, setAnmeldungen] = useState([]);
  const [geladen, setGeladen] = useState(false);
  const [suche, setSuche] = useState("");
  const [sortierung, setSortierung] = useState({ spalte: "kind", richtung: 1 });
  const [spaltenFilter, setSpaltenFilter] = useState({});
  const [beitrag, setBeitrag] = useState(0);
  const [beitragEingabe, setBeitragEingabe] = useState("");
  const [editId, setEditId] = useState(undefined); // undefined = zu (kein Editor offen), null = neu, string = bearbeiten
  const [editDaten, setEditDaten] = useState(REGISTRIERUNG_LEER);
  const [meldung, setMeldung] = useState("");
  const [aktivesLager, setAktivesLager] = useState(undefined); // { lagerId, lagerName } | null
  const [nichtZugeordnet, setNichtZugeordnet] = useState([]);
  const linkVoll = typeof window !== "undefined"
    ? `${window.location.origin}${REGISTRIERUNG_URL}?lager=${encodeURIComponent(lagerId)}`
    : REGISTRIERUNG_URL;

  const [ladeFehler, setLadeFehler] = useState("");

  useEffect(() => {
    if (!lagerId) return;
    const q = query(collection(db, "ferienlager_registrations"), where("lagerId", "==", lagerId), orderBy("submittedAt", "desc"));
    return onSnapshot(q, (snap) => {
      setAnmeldungen(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setGeladen(true);
    }, (err) => { setLadeFehler(err.message); setGeladen(true); });
  }, [lagerId]);

  useEffect(() => {
    if (!kannAktivSetzen) return;
    return onSnapshot(doc(db, "settings", "ferienlagerAktuell"), (snap) => setAktivesLager(snap.exists() ? snap.data() : null));
  }, [kannAktivSetzen]);

  // Anmeldungen aus der Zeit vor der Lager-Zuordnung (kein lagerId-Feld) – ohne diese Liste
  // wären sie in keiner Lager-Ansicht mehr auffindbar. Nur für Admin, damit sie einem Lager
  // zugeordnet oder gelöscht werden können.
  useEffect(() => {
    if (!kannAktivSetzen) return;
    return onSnapshot(collection(db, "ferienlager_registrations"), (snap) => {
      setNichtZugeordnet(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((r) => !r.lagerId));
    });
  }, [kannAktivSetzen]);

  // Teilnehmerbeitrag je Lager (settings/ferienlagerBeitraege = { [lagerId]: Betrag }) – liegt in
  // settings, damit auch die Orga-Rolle (ohne Zugriff auf "ferienlager") ihn lesen kann.
  useEffect(() => {
    if (!lagerId) return;
    return onSnapshot(doc(db, "settings", "ferienlagerBeitraege"), (snap) => {
      const b = snap.exists() ? Number(snap.data()[lagerId]) || 0 : 0;
      setBeitrag(b);
      setBeitragEingabe(b ? String(b) : "");
    });
  }, [lagerId]);

  const beitragSpeichern = () => setDoc(doc(db, "settings", "ferienlagerBeitraege"), { [lagerId]: Number(String(beitragEingabe).replace(",", ".")) || 0 }, { merge: true });

  const zuordnen = (id) => updateDoc(doc(db, "ferienlager_registrations", id), { lagerId });

  const vollBezahlt = (r) => beitrag > 0 && bezahltSumme(r) >= beitrag;
  const zahlungenText = (r) => (r.zahlungen || []).map((z) => `${euro(Number(z.betrag) || 0)} ${datumAnzeige(z.datum)}`).join(" ");

  // Spalten der Tabelle: sortWert für die Sortierung per Klick auf den Kopf, filter "text" oder
  // eine Auswahlliste für die Filterzeile unter dem Kopf.
  const SPALTEN = [
    { key: "eingang", label: "Eingang", sortWert: (r) => eingangMs(r.submittedAt), filter: "text", text: (r) => zeitstempelAnzeige(r.submittedAt) },
    { key: "kind", label: "Kind", sortWert: (r) => `${r.childLastName} ${r.childFirstName}`.trim().toLowerCase(), filter: "text", text: (r) => `${r.childFirstName} ${r.childLastName} ${r.childGender}` },
    { key: "alter", label: "Alter", sortWert: (r) => alterVonGeburtsdatum(r.childBirthdate) ?? -1, filter: "text", text: (r) => String(alterVonGeburtsdatum(r.childBirthdate) ?? "") },
    { key: "kontakt", label: "Kontakt", sortWert: (r) => (r.guardianName || "").toLowerCase(), filter: "text", text: (r) => `${r.guardianName} ${r.phone} ${r.email}` },
    { key: "ort", label: "Ort", sortWert: (r) => (r.city || "").toLowerCase(), filter: "text", text: (r) => r.city || "" },
    { key: "gruppe", label: "Gruppe", sortWert: (r) => (r.group || r.groupWishes || "").toLowerCase(), filter: "text", text: (r) => `${r.group || ""} ${r.groupWishes || ""}` },
    { key: "tshirt", label: "T-Shirt", sortWert: (r) => r.tshirtSize || "", filter: "text", text: (r) => r.tshirtSize || "" },
    { key: "bestaetigt", label: "Bestätigt", sortWert: (r) => (r.confirmed ? 1 : 0), filter: [["", "Alle"], ["ja", "Ja"], ["nein", "Nein"]], passt: (r, f) => (f === "ja" ? !!r.confirmed : !r.confirmed) },
    { key: "zahlungen", label: "Zahlungen", sortWert: (r) => (r.zahlungen || []).map((z) => z.datum).sort().pop() || "", filter: "text", text: zahlungenText },
    { key: "bezahlt", label: "Bezahlt", sortWert: bezahltSumme, rechts: true },
    { key: "differenz", label: "Differenz", sortWert: (r) => differenz(r, beitrag) ?? 0, rechts: true, filter: [["", "Alle"], ["offen", "Offen"], ["ueber", "Überzahlt"], ["null", "Ausgeglichen"]], passt: (r, f) => { const d = differenz(r, beitrag) ?? 0; return f === "offen" ? d < 0 : f === "ueber" ? d > 0 : d === 0; } },
    { key: "status", label: "✓", sortWert: (r) => (vollBezahlt(r) ? 1 : 0), filter: [["", "Alle"], ["ja", "✓"], ["nein", "offen"]], passt: (r, f) => (f === "ja" ? vollBezahlt(r) : !vollBezahlt(r)) },
  ];

  const sortieren = (key) => setSortierung((alt) => ({ spalte: key, richtung: alt.spalte === key ? -alt.richtung : 1 }));

  const gefiltert = anmeldungen.filter((r) => {
    for (const sp of SPALTEN) {
      const f = spaltenFilter[sp.key];
      if (!f) continue;
      if (sp.filter === "text" ? !sp.text(r).toLowerCase().includes(f.toLowerCase()) : !sp.passt(r, f)) return false;
    }
    if (!suche.trim()) return true;
    const hay = [r.childFirstName, r.childLastName, r.city, r.groupWishes, r.group, r.guardianName].join(" ").toLowerCase();
    return hay.includes(suche.trim().toLowerCase());
  });
  const sortSpalte = SPALTEN.find((sp) => sp.key === sortierung.spalte);
  if (sortSpalte) {
    gefiltert.sort((a, b) => {
      const x = sortSpalte.sortWert(a), y = sortSpalte.sortWert(b);
      return (typeof x === "number" ? x - y : String(x).localeCompare(String(y), "de")) * sortierung.richtung;
    });
  }

  const linkKopieren = async () => {
    try { await navigator.clipboard.writeText(linkVoll); setMeldung("Link kopiert!"); setTimeout(() => setMeldung(""), 2000); }
    catch { setMeldung(linkVoll); }
  };

  const alsAktivFestlegen = async () => {
    await setDoc(doc(db, "settings", "ferienlagerAktuell"), { lagerId, lagerName });
  };

  const oeffnen = (r) => {
    setEditId(r ? r.id : null);
    setEditDaten({ ...REGISTRIERUNG_LEER, ...(r || {}), eingangDatum: lokalesDatum(r?.submittedAt ? eingangMs(r.submittedAt) : Date.now()) });
  };
  const schliessen = () => { setEditId(undefined); setEditDaten(REGISTRIERUNG_LEER); };

  const speichern = async () => {
    if (!editDaten.childFirstName.trim() || !editDaten.childLastName.trim()) { alert("Vor- und Nachname sind Pflicht."); return; }
    const { id, submittedAt, eingangDatum, ...felder } = editDaten;
    // Eingangsdatum: Uhrzeit bleibt erhalten, solange der Tag nicht geändert wurde
    const bisher = submittedAt ? eingangMs(submittedAt) : null;
    const eingang = bisher && lokalesDatum(bisher) === eingangDatum ? null
      : eingangDatum ? new Date(`${eingangDatum}T${bisher ? new Date(bisher).toTimeString().slice(0, 5) : new Date().toTimeString().slice(0, 5)}`) : new Date();
    if (editId) {
      await updateDoc(doc(db, "ferienlager_registrations", editId), eingang ? { ...felder, submittedAt: eingang } : felder);
    } else {
      await addDoc(collection(db, "ferienlager_registrations"), { ...felder, lagerId, submittedAt: eingang || new Date() });
    }
    schliessen();
  };

  const loeschen = async () => {
    if (!editId || !window.confirm("Diese Anmeldung wirklich unwiderruflich löschen?")) return;
    await deleteDoc(doc(db, "ferienlager_registrations", editId));
    schliessen();
  };

  const quickUpdate = (id, feld, wert) => updateDoc(doc(db, "ferienlager_registrations", id), { [feld]: wert });

  const exportExcel = () => {
    if (gefiltert.length === 0) { alert("Keine Anmeldungen zum Exportieren."); return; }
    const rows = gefiltert.map((r) => ({
      "Angemeldet am": zeitstempelAnzeige(r.submittedAt),
      "Vorname Kind": r.childFirstName || "", "Nachname Kind": r.childLastName || "",
      "Geschlecht": r.childGender || "", "Geburtsdatum": datumAnzeige(r.childBirthdate),
      "Alter": alterVonGeburtsdatum(r.childBirthdate) ?? "",
      "Erziehungsberechtigte": r.guardianName || "", "Straße": r.street || "", "PLZ": r.zip || "", "Ort": r.city || "",
      "Telefon": r.phone || "", "E-Mail": r.email || "", "Gruppenwünsche": r.groupWishes || "", "T-Shirt-Größe": r.tshirtSize || "",
      "Sonstiges (Eltern)": r.notes || "",
      "Teilnehmerbeitrag": beitrag || "", "Zahlungen": zahlungenText(r), "Bezahlt": bezahltSumme(r),
      "Differenz": differenz(r, beitrag) ?? "", "Vollständig bezahlt": vollBezahlt(r) ? "Ja" : "Nein",
      "Förderung": r.funding || "", "Krankheiten": r.illnesses || "", "Sonstige Hinweise (Admin)": r.adminNotes || "",
      "Anmeldung bestätigt": r.confirmed ? "Ja" : "Nein", "Gruppe": r.group || "", "Sonstiges (Admin)": r.miscAdmin || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Anmeldungen");
    XLSX.writeFile(wb, `Ferienlager-Anmeldungen_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const gesamt = anmeldungen.length;
  const bestaetigt = anmeldungen.filter((r) => r.confirmed).length;
  const vollstaendig = anmeldungen.filter(vollBezahlt).length;
  const eingegangen = anmeldungen.reduce((a, r) => a + bezahltSumme(r), 0);
  const offenGesamt = beitrag ? anmeldungen.reduce((a, r) => a + Math.max(0, beitrag - bezahltSumme(r)), 0) : 0;

  return (
    <div>
      <div style={s.budget}>
        <div style={{ ...s.budgetBox, backgroundColor: "#eaf1fc" }}><div style={{ fontSize: "1.4rem", fontWeight: 700, color: "#1a3558" }}>{gesamt}</div><div style={{ fontSize: "0.75rem", color: "#666" }}>Anmeldungen</div></div>
        <div style={{ ...s.budgetBox, backgroundColor: "#e6f5ec" }}><div style={{ fontSize: "1.4rem", fontWeight: 700, color: "#2f7d54" }}>{bestaetigt}</div><div style={{ fontSize: "0.75rem", color: "#666" }}>Bestätigt</div></div>
        <div style={{ ...s.budgetBox, backgroundColor: "#e6f5ec" }}><div style={{ fontSize: "1.4rem", fontWeight: 700, color: "#2f7d54" }}>{vollstaendig}</div><div style={{ fontSize: "0.75rem", color: "#666" }}>Vollständig bezahlt</div></div>
        <div style={{ ...s.budgetBox, backgroundColor: "#eaf1fc" }}><div style={{ fontSize: "1.4rem", fontWeight: 700, color: "#1a3558" }}>{euro(eingegangen)}</div><div style={{ fontSize: "0.75rem", color: "#666" }}>Eingegangen</div></div>
        <div style={{ ...s.budgetBox, backgroundColor: "#fff4e5" }}><div style={{ fontSize: "1.4rem", fontWeight: 700, color: "#92400e" }}>{euro(offenGesamt)}</div><div style={{ fontSize: "0.75rem", color: "#666" }}>Noch offen</div></div>
      </div>

      <div style={{ ...s.card, marginBottom: "1rem" }}>
        <div style={s.row}>
          <span style={{ fontSize: "0.85rem" }}>🔗 Anmeldelink für {lagerName}:</span>
          <code style={{ fontSize: "0.8rem", background: "#f0f4ff", padding: "0.2rem 0.5rem", borderRadius: "4px" }}>{linkVoll}</code>
          <button style={s.btnSm} onClick={linkKopieren}>Kopieren</button>
          {meldung && <span style={{ fontSize: "0.78rem", color: "#2f7d54" }}>{meldung}</span>}
        </div>
        <div style={{ ...s.row, marginTop: "0.5rem" }}>
          <span style={{ fontSize: "0.85rem" }}>💶 Teilnehmerbeitrag {lagerName}:</span>
          {kannAktivSetzen ? (
            <>
              <input style={{ ...s.input, width: "90px", textAlign: "right" }} inputMode="decimal" value={beitragEingabe} onChange={(e) => setBeitragEingabe(e.target.value)} placeholder="€" />
              <span style={{ fontSize: "0.85rem" }}>€</span>
              {String(beitrag || "") !== String(beitragEingabe).replace(",", ".") && <button style={s.btnSm} onClick={beitragSpeichern}>Speichern</button>}
            </>
          ) : <b style={{ fontSize: "0.85rem" }}>{beitrag ? euro(beitrag) : "nicht festgelegt"}</b>}
        </div>
        {kannAktivSetzen && aktivesLager !== undefined && (
          <div style={{ ...s.row, marginTop: "0.5rem" }}>
            {aktivesLager?.lagerId === lagerId ? (
              <span style={{ fontSize: "0.78rem", color: "#2f7d54" }}>✓ Dies ist aktuell das Lager, für das der allgemeine Anmeldelink (ohne Lager-Angabe) läuft.</span>
            ) : (
              <>
                <span style={{ fontSize: "0.78rem", color: "#888" }}>
                  {aktivesLager ? `Aktuell aktiv: ${aktivesLager.lagerName}` : "Noch kein Lager als aktiv markiert."}
                </span>
                <button style={s.btnSm} onClick={alsAktivFestlegen}>📍 {lagerName} als aktuelles Anmeldungs-Lager festlegen</button>
              </>
            )}
          </div>
        )}
      </div>

      {kannAktivSetzen && nichtZugeordnet.length > 0 && (
        <div style={{ ...s.card, marginBottom: "1rem", backgroundColor: "#fff4e5" }}>
          <div style={{ fontSize: "0.85rem", marginBottom: "0.5rem" }}>
            ⚠️ {nichtZugeordnet.length} Anmeldung(en) aus der Zeit vor der Lager-Zuordnung sind noch keinem Lager zugeordnet und tauchen daher in keiner Lager-Ansicht auf:
          </div>
          {nichtZugeordnet.map((r) => (
            <div key={r.id} style={{ ...s.row, marginBottom: "0.3rem" }}>
              <span style={{ fontSize: "0.83rem", flex: 1 }}>{r.childFirstName} {r.childLastName} <span style={{ color: "#999" }}>({zeitstempelAnzeige(r.submittedAt)})</span></span>
              <button style={s.btnSm} onClick={() => zuordnen(r.id)}>→ {lagerName} zuordnen</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ ...s.row, marginBottom: "0.8rem" }}>
        <input style={{ ...s.input, flex: 2, minWidth: "180px" }} placeholder="Suche nach Name, Ort, Gruppe …" value={suche} onChange={(e) => setSuche(e.target.value)} />
        {Object.values(spaltenFilter).some(Boolean) && <button style={s.btnSm} onClick={() => setSpaltenFilter({})}>Filter zurücksetzen</button>}
        <span style={{ flex: 1 }} />
        <button style={s.btnGreen} onClick={exportExcel}>⬇ Excel-Export</button>
        <button style={s.btn} onClick={() => oeffnen(null)}>+ Neue Anmeldung</button>
      </div>

      {ladeFehler && <p style={{ color: "#c0392b", background: "#fdecea", padding: "0.6rem 0.8rem", borderRadius: "6px" }}>Fehler beim Laden: {ladeFehler}</p>}
      {!geladen && <p style={{ color: "#999", textAlign: "center" }}>Lädt …</p>}
      {geladen && !ladeFehler && anmeldungen.length === 0 && <p style={{ color: "#999", textAlign: "center" }}>Keine Anmeldungen gefunden.</p>}

      {anmeldungen.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={s.table}>
            <thead>
              <tr>
                {SPALTEN.map((sp) => (
                  <th key={sp.key} style={{ ...s.th, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", textAlign: sp.rechts ? "right" : "left" }} onClick={() => sortieren(sp.key)} title="Sortieren">
                    {sp.label}{sortierung.spalte === sp.key ? (sortierung.richtung === 1 ? " ▲" : " ▼") : ""}
                  </th>
                ))}
                <th style={s.th}></th>
              </tr>
              <tr>
                {SPALTEN.map((sp) => (
                  <th key={sp.key} style={{ padding: "0.3rem", backgroundColor: "#eef2f8" }}>
                    {sp.filter === "text" && (
                      <input style={{ ...s.input, width: "100%", minWidth: "50px", boxSizing: "border-box", padding: "0.25rem 0.4rem", fontSize: "0.78rem" }} placeholder="Filter"
                        value={spaltenFilter[sp.key] || ""} onChange={(e) => setSpaltenFilter({ ...spaltenFilter, [sp.key]: e.target.value })} />
                    )}
                    {Array.isArray(sp.filter) && (
                      <select style={{ ...s.select, width: "100%", padding: "0.25rem", fontSize: "0.78rem" }} value={spaltenFilter[sp.key] || ""} onChange={(e) => setSpaltenFilter({ ...spaltenFilter, [sp.key]: e.target.value })}>
                        {sp.filter.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    )}
                  </th>
                ))}
                <th style={{ backgroundColor: "#eef2f8" }}></th>
              </tr>
            </thead>
            <tbody>
              {gefiltert.length === 0 && <tr><td colSpan={SPALTEN.length + 1} style={{ ...s.td, color: "#999", textAlign: "center" }}>Keine Anmeldungen passen zu den Filtern.</td></tr>}
              {gefiltert.map((r) => (
                <tr key={r.id}>
                  <td style={{ ...s.td, whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                    {r.submittedAt ? <>{zeitstempelAnzeige(r.submittedAt).split(" ")[0]}<br /><span style={{ color: "#999" }}>{zeitstempelAnzeige(r.submittedAt).split(" ")[1]}</span></> : "–"}
                  </td>
                  <td style={s.td}><b>{r.childFirstName} {r.childLastName}</b><br /><span style={{ color: "#999", fontSize: "0.75rem" }}>{r.childGender}</span></td>
                  <td style={s.td}>{alterVonGeburtsdatum(r.childBirthdate) ?? "–"}</td>
                  <td style={s.td}>{r.guardianName}<br /><span style={{ color: "#999", fontSize: "0.75rem" }}>{r.phone}</span></td>
                  <td style={s.td}>{r.city}</td>
                  <td style={s.td}>{r.group || r.groupWishes}</td>
                  <td style={s.td}>{r.tshirtSize}</td>
                  <td style={s.td}><input type="checkbox" checked={!!r.confirmed} onChange={(e) => quickUpdate(r.id, "confirmed", e.target.checked)} /></td>
                  <td style={{ ...s.td, whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                    {(r.zahlungen || []).slice().sort((a, b) => (a.datum || "").localeCompare(b.datum || "")).map((z) => (
                      <div key={z.id} title={z.notiz || ""}>{euro(Number(z.betrag) || 0)} <span style={{ color: "#999" }}>{datumAnzeige(z.datum)}</span></div>
                    ))}
                  </td>
                  <td style={{ ...s.td, textAlign: "right", whiteSpace: "nowrap" }}>{euro(bezahltSumme(r))}</td>
                  <td style={{ ...s.td, textAlign: "right", whiteSpace: "nowrap", fontWeight: 600, color: (differenz(r, beitrag) ?? 0) < 0 ? "#c0392b" : (differenz(r, beitrag) ?? 0) > 0 ? "#1a73e8" : "#2f7d54" }}>
                    {differenz(r, beitrag) == null ? "–" : euro(differenz(r, beitrag))}
                  </td>
                  <td style={{ ...s.td, textAlign: "center", fontSize: "1.1rem", color: "#2f7d54", fontWeight: 700 }}>{vollBezahlt(r) ? "✔" : ""}</td>
                  <td style={s.td}><button style={s.btnSm} onClick={() => oeffnen(r)}>Bearbeiten</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editId !== undefined && (
        <div style={{ ...s.card, marginTop: "1rem" }}>
          <h3 style={s.h3}>{editId ? "Anmeldung bearbeiten" : "Neue Anmeldung"}</h3>
          <AnmeldungFormular daten={editDaten} onChange={setEditDaten} beitrag={beitrag} />
          <div style={s.row}>
            {editId && <button style={s.btnDanger} onClick={loeschen}>Löschen</button>}
            <span style={{ flex: 1 }} />
            <button style={s.btnSm} onClick={schliessen}>Abbrechen</button>
            <button style={s.btnGreen} onClick={speichern}>💾 Speichern</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Hauptkomponente ────────────────────────────────────────────────────────────
const TABS = [
  { id: "speiseplan", label: "🍽️ Speiseplan" },
  { id: "einkauf", label: "🛒 Einkaufsliste" },
  { id: "budget", label: "💰 Budget" },
  { id: "todo", label: "✅ ToDo" },
  { id: "gerichte", label: "🍴 Gerichte" },
  { id: "kuechenartikel", label: "🍳 Küchenartikel" },
  { id: "lebensmittel", label: "🥫 Lebensmittel vorhanden" },
  { id: "anmeldungen", label: "👨‍👩‍👧 Anmeldungen" },
];

// Eingeschränkte Orga-Rolle: hat keinen Lesezugriff auf die Collection "ferienlager" (siehe
// firestore.rules), erfährt das aktuell offene Lager daher nur über den öffentlich lesbaren
// Zeiger settings/ferienlagerAktuell.
function AnmeldungenNurRolle() {
  const [aktivesLager, setAktivesLager] = useState(undefined); // undefined = lädt, null = keins gesetzt

  useEffect(() => {
    return onSnapshot(doc(db, "settings", "ferienlagerAktuell"), (snap) => setAktivesLager(snap.exists() ? snap.data() : null));
  }, []);

  if (aktivesLager === undefined) return <Seite titel="🏕️ Ferienlager – Anmeldungen"><p style={{ color: "#999" }}>Lädt …</p></Seite>;
  if (!aktivesLager) {
    return (
      <Seite titel="🏕️ Ferienlager – Anmeldungen">
        <p style={{ color: "#999" }}>Es ist aktuell kein Lager als „aktiv" markiert. Bitte beim Admin melden.</p>
      </Seite>
    );
  }
  return (
    <Seite titel={`🏕️ Anmeldungen – ${aktivesLager.lagerName}`}>
      <AnmeldungenTab lagerId={aktivesLager.lagerId} lagerName={aktivesLager.lagerName} kannAktivSetzen={false} />
    </Seite>
  );
}

export default function Ferienlager({ user, nurAnmeldungen }) {
  const [lager, setLager] = useState(null);
  const [aktiverTab, setAktiverTab] = useState("speiseplan");
  const [gerichte, setGerichte] = useState([]);
  const [artikel, setArtikel] = useState([]);
  const [gerichteGeladen, setGerichteGeladen] = useState(false);
  const [alleLager, setAlleLager] = useState([]);
  const [kopierQuelle, setKopierQuelle] = useState("");
  const [aktivesLagerOben, setAktivesLagerOben] = useState(null);

  // Aktiv markiertes Lager – wird oben im Ferienlager-Bereich immer angezeigt (Link
  // schnell greifbar, ohne erst in ein Lager reinklicken zu müssen).
  useEffect(() => {
    if (nurAnmeldungen) return;
    return onSnapshot(doc(db, "settings", "ferienlagerAktuell"), (snap) => setAktivesLagerOben(snap.exists() ? snap.data() : null));
  }, [nurAnmeldungen]);

  // Die Planungs-Referenzlisten (Gerichte/Artikel) sind Admin-only – für die eingeschränkte
  // Anmeldungen-Rolle weder nötig noch (per Firestore-Regeln) lesbar, daher übersprungen.
  useEffect(() => {
    if (nurAnmeldungen) return;
    const q = query(collection(db, "ferienlager_gerichte"), where("uid", "==", user.uid));
    return onSnapshot(q, async (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (data.length === 0 && !gerichteGeladen) {
        for (const g of STANDARD_GERICHTE) {
          await addDoc(collection(db, "ferienlager_gerichte"), { ...g, uid: user.uid, erstellt: new Date() });
        }
        setGerichteGeladen(true);
      } else {
        data.sort((a, b) => a.name.localeCompare(b.name));
        setGerichte(data);
        setGerichteGeladen(true);
      }
    });
  }, [user.uid, gerichteGeladen, nurAnmeldungen]);

  useEffect(() => {
    if (nurAnmeldungen) return;
    const q = query(collection(db, "ferienlager_artikel"), where("uid", "==", user.uid));
    // Einmalige Prüfung: falls leer, Standard-Artikel eintragen
    getDocs(q).then(async (snap) => {
      if (snap.empty) {
        for (const a of STANDARD_ARTIKEL) {
          await addDoc(collection(db, "ferienlager_artikel"), { ...a, uid: user.uid });
        }
      }
    });
    return onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => a.name.localeCompare(b.name));
      setArtikel(data);
    });
  }, [user.uid, nurAnmeldungen]);

  useEffect(() => {
    if (nurAnmeldungen) return;
    const q = query(collection(db, "ferienlager"), where("uid", "==", user.uid));
    return onSnapshot(q, (snap) => setAlleLager(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, [user.uid, nurAnmeldungen]);

  const uebernehmen = async () => {
    const quelle = alleLager.find((l) => l.id === kopierQuelle);
    if (!quelle) return;
    if (!window.confirm(`Speiseplan, Einkaufsliste, ToDo, Budget, Küchenartikel und vorhandene Lebensmittel von "${quelle.name}" übernehmen? Der aktuelle Stand in "${lager.name}" wird dabei überschrieben.`)) return;
    const patch = {
      speiseplan: quelle.speiseplan || [], einkaufsliste: quelle.einkaufsliste || [], todos: quelle.todos || [], budget: quelle.budget || 0,
      kuechenartikel: quelle.kuechenartikel || [], lebensmittelVorhanden: quelle.lebensmittelVorhanden || [],
    };
    await updateDoc(doc(db, "ferienlager", lager.id), patch);
    setLager({ ...lager, ...patch });
    setKopierQuelle("");
  };

  // Eingeschränkte Rolle: nur die Anmeldungen des aktuell aktiven Lagers, kein Lager-Kontext,
  // keine Planung.
  if (nurAnmeldungen) {
    return <AnmeldungenNurRolle />;
  }

  const linkOben = aktivesLagerOben ? `${window.location.origin}${REGISTRIERUNG_URL}?lager=${encodeURIComponent(aktivesLagerOben.lagerId)}` : "";

  return (
    <Seite titel="🏕️ Ferienlager">
      <div style={{ ...s.card, marginBottom: "1rem", backgroundColor: aktivesLagerOben ? "#e6f5ec" : "#fff4e5" }}>
        {aktivesLagerOben ? (
          <div style={s.row}>
            <span style={{ fontSize: "0.85rem" }}>🔗 Aktueller Anmeldelink ({aktivesLagerOben.lagerName}):</span>
            <code style={{ fontSize: "0.8rem", background: "#fff", padding: "0.2rem 0.5rem", borderRadius: "4px" }}>{linkOben}</code>
            <button style={s.btnSm} onClick={() => navigator.clipboard.writeText(linkOben).catch(() => {})}>Kopieren</button>
          </div>
        ) : (
          <span style={{ fontSize: "0.85rem", color: "#92400e" }}>⚠️ Noch kein Lager als „aktiv" markiert – Anmeldungen sind aktuell nur über den lagerspezifischen Link möglich. Im Anmeldungen-Tab eines Lagers festlegen.</span>
        )}
      </div>
      {!lager ? <LagerAuswahl user={user} onSelect={setLager} /> : (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
            <div style={{ fontSize: "0.85rem", color: "#888" }}>
              <b style={{ color: "#1a3558" }}>{lager.name}</b>{" "}
              {lager.von && lager.bis ? `· ${lager.von} – ${lager.bis}` : ""}{lager.teilnehmer ? ` · ${lager.teilnehmer} Personen` : ""}
            </div>
            <button style={s.btnSm} onClick={() => setLager(null)}>← Lager wechseln</button>
          </div>

          {alleLager.length > 1 && (
            <div style={{ ...s.row, marginBottom: "0.8rem" }}>
              <span style={{ fontSize: "0.8rem", color: "#666" }}>📋 Speiseplan/Einkauf/ToDo/Budget übernehmen von:</span>
              <select style={s.select} value={kopierQuelle} onChange={(e) => setKopierQuelle(e.target.value)}>
                <option value="">Anderes Lager wählen …</option>
                {alleLager.filter((l) => l.id !== lager.id).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <button style={s.btnSm} disabled={!kopierQuelle} onClick={uebernehmen}>Übernehmen</button>
            </div>
          )}

          <div style={s.tabs}>
            {TABS.map((t) => (
              <button key={t.id} style={{ ...s.tab, ...(aktiverTab === t.id ? s.tabAktiv : {}) }} onClick={() => setAktiverTab(t.id)}>{t.label}</button>
            ))}
          </div>
          {aktiverTab === "speiseplan" && <SpeiseplanTab lager={lager} gerichte={gerichte} onUpdate={setLager} />}
          {aktiverTab === "einkauf" && <EinkaufslisteTab lager={lager} artikel={artikel} uid={user.uid} onUpdate={setLager} />}
          {aktiverTab === "budget" && <BudgetTab lager={lager} onUpdate={setLager} />}
          {aktiverTab === "todo" && <TodoTab lager={lager} onUpdate={setLager} />}
          {aktiverTab === "gerichte" && <GerichteTab gerichte={gerichte} artikel={artikel} user={user} lager={lager} onUpdate={setLager} />}
          {aktiverTab === "kuechenartikel" && <KuechenartikelTab lager={lager} onUpdate={setLager} />}
          {aktiverTab === "lebensmittel" && <LebensmittelVorhandenTab lager={lager} onUpdate={setLager} />}
          {aktiverTab === "anmeldungen" && <AnmeldungenTab lagerId={lager.id} lagerName={lager.name} kannAktivSetzen />}
        </div>
      )}
    </Seite>
  );
}
